import {
  type ChangeEvent,
  type MouseEvent,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ALargeSmall,
  ArrowLeft,
  AtSign,
  Baseline,
  Bold,
  Braces,
  ChevronDown,
  Code,
  Copy,
  Download,
  Hash,
  IndentDecrease,
  IndentIncrease,
  Info,
  Italic,
  Link2,
  List,
  ListOrdered,
  Lock,
  MoreHorizontal,
  MoreVertical,
  Pencil,
  Paperclip,
  Plus,
  Quote,
  Reply,
  Search,
  Send,
  Smile,
  SmilePlus,
  Star,
  Strikethrough,
  Trash2,
  Users,
  X,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import { Avatar, AvatarFallback } from "../../components/ui/avatar";
import { Button } from "../../components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { message as antdMessage } from "antd";
import { usePtsZulipUnread } from "../../context/PtsZulipUnreadContext";
import { toBasicHeader, toZulipUserId } from "../../utils/zulipAuth";
import "../../styles/style.css";

type ConversationType = "channel" | "dm";

type Conversation = {
  id: string;
  name: string;
  type: ConversationType;
  unread?: number;
  isPrivate?: boolean;
  zulipUserId?: number;
  streamId?: number;
};

type ChatMessage = {
  id: string;
  conversationId: string;
  author: string;
  authorCode: string;
  tone: "violet" | "green" | "orange";
  text: string;
  htmlContent?: string;
  timeLabel: string;
  sortKey?: number;
  /** Zulip sender user id — used so only your messages show edit/delete */
  senderZulipUserId?: number;
  reactions?: { emoji: string; count: number; reactedByMe?: boolean }[];
  /** Present for Zulip-backed rows when the current user has starred the message */
  starredByMe?: boolean;
};

type ZulipStream = {
  stream_id: number;
  name: string;
  invite_only?: boolean;
};

type ZulipStreamsResponse = {
  result: string;
  msg: string;
  streams: ZulipStream[];
};

type ZulipUser = {
  user_id: number;
  full_name: string;
  email?: string;
  delivery_email?: string;
  is_active: boolean;
  is_bot?: boolean;
};

type ZulipUsersResponse = {
  result: string;
  msg: string;
  members?: ZulipUser[];
  users?: ZulipUser[];
};

type ZulipMessage = {
  id: number;
  sender_id: number;
  sender_full_name: string;
  content: string;
  timestamp: number;
  type?: "stream" | "private" | "direct";
  display_recipient?: string | Array<{ id: number; full_name: string }>;
  reactions?: Array<{
    emoji_name: string;
    user_id: number;
  }>;
  /** User-specific flags from Zulip (e.g. `read`, `starred`) */
  flags?: string[];
};

type ZulipMessagesResponse = {
  result: string;
  msg: string;
  messages: ZulipMessage[];
};

type ZulipRegisterResponse = {
  result: string;
  msg: string;
  queue_id: string;
  last_event_id: number;
};

type ZulipEvent = {
  id: number;
  type: string;
  op?: "add" | "remove";
  /** Legacy name for `op` on some `update_message_flags` payloads */
  operation?: "add" | "remove";
  user_id?: number;
  email?: string;
  server_timestamp?: number;
  presence?: Record<string, ZulipPresenceEntry>;
  message_id?: number;
  message_ids?: number[];
  content?: string;
  emoji_name?: string;
  /** Present on `subscription` events when `op` is `add` or `remove` */
  subscriptions?: Array<{
    stream_id?: number;
    name?: string;
    invite_only?: boolean;
  }>;
  message?: {
    id: number;
    sender_id: number;
    sender_full_name: string;
    content: string;
    timestamp: number;
    type: "stream" | "private";
    stream_id?: number;
    display_recipient?: string | Array<{ id: number; full_name: string }>;
    subject?: string;
    reactions?: Array<{
      emoji_name: string;
      user_id: number;
    }>;
    flags?: string[];
  };
  /** `update_message_flags` — message ids affected */
  messages?: number[];
  flag?: string;
};

type ZulipEventsResponse = {
  result: string;
  msg: string;
  events: ZulipEvent[];
};

type ZulipUploadResponse = {
  result: string;
  msg: string;
  uri?: string;
  url?: string;
};

type ZulipSubscription = {
  name: string;
  stream_id?: number;
  invite_only?: boolean;
  subscribers?: number[];
};

type ZulipSubscriptionsResponse = {
  result: string;
  msg: string;
  subscriptions?: ZulipSubscription[];
};

type ZulipPresenceEntry = {
  status?: "active" | "idle" | "offline";
  timestamp?: number;
};

type ZulipPresenceResponse = {
  result: string;
  msg: string;
  presences?: Record<string, Record<string, ZulipPresenceEntry> | undefined>;
};

type MentionOption = {
  id: string;
  label: string;
  mentionText: string;
};

type ZulipTopic = {
  name: string;
  max_id?: number;
};

type ZulipTopicsResponse = {
  result: string;
  msg: string;
  topics: ZulipTopic[];
};

/** Reads Zulip-style JSON errors (`msg`) or plain text from a failed response body. */
async function readZulipApiErrorMessage(
  response: Response,
  fallback?: string
): Promise<string> {
  const defaultMsg = fallback ?? `Something went wrong (${response.status}).`;
  let body = "";
  try {
    body = await response.text();
  } catch {
    return defaultMsg;
  }
  const trimmed = body.trim();
  if (!trimmed) {
    return defaultMsg;
  }
  try {
    const data = JSON.parse(trimmed) as { msg?: string; message?: string };
    if (typeof data.msg === "string" && data.msg.trim()) {
      return data.msg.trim();
    }
    if (typeof data.message === "string" && data.message.trim()) {
      return data.message.trim();
    }
  } catch {
    /* non-JSON body */
  }
  return trimmed.length <= 400 ? trimmed : defaultMsg;
}

/** Parse `VITE_*` booleans: unset → defaultValue; true/1/yes/on vs false/0/no/off */
function viteEnvBool(value: string | undefined, defaultValue = true): boolean {
  if (value === undefined || value === "") {
    return defaultValue;
  }
  const v = value.trim().toLowerCase();
  if (["false", "0", "no", "off"].includes(v)) {
    return false;
  }
  if (["true", "1", "yes", "on"].includes(v)) {
    return true;
  }
  return defaultValue;
}

const QUICK_EMOJIS = ["😀", "👍", "🎉", "🔥", "✅", "👏"];
const REACTION_PICKER_EMOJIS = [
  { emoji: "😀", name: "grinning" },
  { emoji: "😃", name: "smiley" },
  { emoji: "😄", name: "smile" },
  { emoji: "😁", name: "grin" },
  { emoji: "😆", name: "laughing" },
  { emoji: "😂", name: "joy" },
  { emoji: "😊", name: "blush" },
  { emoji: "😉", name: "wink" },
  { emoji: "😍", name: "heart_eyes" },
  { emoji: "😘", name: "kissing_heart" },
  { emoji: "😎", name: "sunglasses" },
  { emoji: "🤔", name: "thinking" },
  { emoji: "👍", name: "thumbs_up" },
  { emoji: "👎", name: "thumbs_down" },
  { emoji: "👏", name: "clap" },
  { emoji: "🔥", name: "fire" },
  { emoji: "🎉", name: "tada" },
  { emoji: "❤️", name: "heart" },
  { emoji: "✅", name: "white_check_mark" },
  { emoji: "🚀", name: "rocket" },
  { emoji: "🥳", name: "partying_face" },
  { emoji: "🐙", name: "octopus" },
  { emoji: "🛠️", name: "hammer_and_wrench" },
  { emoji: "😅", name: "sweat_smile" },
  { emoji: "👀", name: "eyes" },
  { emoji: "👌", name: "ok_hand" },
  { emoji: "🙏", name: "pray" },
  { emoji: "💯", name: "100" },
  { emoji: "🌍", name: "earth_africa" },
  { emoji: "✨", name: "sparkles" },
  { emoji: "⭐", name: "star" },
  { emoji: "🏗️", name: "building_construction" },
];
const REACTION_PICKER_SECTIONS: Array<{
  title: string;
  emojis: string[];
}> = [
  {
    title: "POPULAR",
    emojis: ["😊", "😆", "❤️", "👍", "🎉", "🔥", "🐙", "🛠️"],
  },
  {
    title: "SMILEYS & EMOTION",
    emojis: ["🙂", "😍", "😅", "😂", "💔", "👀"],
  },
  {
    title: "HANDS & BODY",
    emojis: ["👎", "👌", "🙏", "✅", "💯"],
  },
  {
    title: "OBJECTS & MORE",
    emojis: ["🚀", "🌍", "✨", "⭐", "🏗️"],
  },
];
const EMOJI_NAME_BY_SYMBOL: Record<string, string> = Object.fromEntries(
  REACTION_PICKER_EMOJIS.map(item => [item.emoji, item.name])
);
const SYMBOL_BY_EMOJI_NAME: Record<string, string> = Object.fromEntries(
  REACTION_PICKER_EMOJIS.map(item => [item.name, item.emoji])
);

/** Zulip / common shortcodes not already covered by the reaction picker map */
const EXTRA_ZULIP_EMOJI_BY_NAME: Record<string, string> = {
  "+1": "👍",
  "-1": "👎",
  bow: "🙇",
  raised_hands: "🙌",
  muscle: "💪",
  wave: "👋",
  v: "✌️",
  crossed_fingers: "🤞",
  metal: "🤘",
  coffee: "☕",
  phone: "📞",
  computer: "💻",
  briefcase: "💼",
  calendar: "📅",
  clock: "🕐",
  hourglass: "⌛",
  bulb: "💡",
  zap: "⚡",
  email: "📧",
  package: "📦",
  chart_with_upwards_trend: "📈",
  chart_with_downwards_trend: "📉",
  moneybag: "💰",
  credit_card: "💳",
  construction: "🚧",
  warning: "⚠️",
  no_entry: "⛔",
  x: "❌",
  o: "⭕",
  question: "❓",
  exclamation: "❗",
  bangbang: "‼️",
  interrobang: "⁉️",
  dog: "🐶",
  cat: "🐱",
  snake: "🐍",
  bee: "🐝",
  butterfly: "🦋",
  fish: "🐟",
  turtle: "🐢",
  bird: "🐦",
  penguin: "🐧",
  koala: "🐨",
  panda_face: "🐼",
  unicorn: "🦄",
  horse: "🐴",
  pig: "🐷",
  mouse: "🐭",
  hamster: "🐹",
  rabbit: "🐰",
  chipmunk: "🐿️",
  fox_face: "🦊",
  bear: "🐻",
  lion_face: "🦁",
  tiger: "🐯",
  wolf: "🐺",
  frog: "🐸",
  apple: "🍎",
  pizza: "🍕",
  burger: "🍔",
  fries: "🍟",
  cake: "🍰",
  beer: "🍺",
  wine_glass: "🍷",
  clinking_glasses: "🥂",
  birthday: "🎂",
  gift: "🎁",
  balloon: "🎈",
  confetti_ball: "🎊",
  soccer: "⚽",
  basketball: "🏀",
  football: "🏈",
  tennis: "🎾",
  eight_ball: "🎱",
  dart: "🎯",
  guitar: "🎸",
  musical_note: "🎵",
  headphones: "🎧",
  microphone: "🎤",
  movie_camera: "🎥",
  book: "📖",
  memo: "📝",
  pencil2: "✏️",
  paperclip: "📎",
  pushpin: "📌",
  round_pushpin: "📍",
  link: "🔗",
  chains: "⛓️",
  wrench: "🔧",
  hammer: "🔨",
  nut_and_bolt: "🔩",
  gear: "⚙️",
  mag: "🔍",
  mag_right: "🔎",
  key: "🔑",
  lock: "🔒",
  unlock: "🔓",
  bell: "🔔",
  no_bell: "🔕",
  radio_button: "🔘",
  white_check_mark: "✅",
  negative_squared_cross_mark: "❎",
  heavy_check_mark: "✔️",
  heavy_multiplication_x: "✖️",
  heavy_plus_sign: "➕",
  heavy_minus_sign: "➖",
  heavy_division_sign: "➗",
  arrow_right: "➡️",
  arrow_left: "⬅️",
  arrow_up: "⬆️",
  arrow_down: "⬇️",
  soon: "🔜",
  end: "🔚",
  back: "🔙",
  on: "🔛",
  top: "🔝",
};

const ZULIP_EMOJI_CODEPOINT_BY_NAME: Record<string, string> = {
  ...EXTRA_ZULIP_EMOJI_BY_NAME,
  ...SYMBOL_BY_EMOJI_NAME,
};

const ZULIP_EMOJI_SHORTCODE_SORTED_NAMES = Object.keys(
  ZULIP_EMOJI_CODEPOINT_BY_NAME
).sort((a, b) => b.length - a.length);

const replaceZulipEmojiShortcodes = (raw: string): string => {
  if (!raw.includes(":")) {
    return raw;
  }
  let out = raw;
  for (const name of ZULIP_EMOJI_SHORTCODE_SORTED_NAMES) {
    const sym = ZULIP_EMOJI_CODEPOINT_BY_NAME[name];
    if (!sym) {
      continue;
    }
    const esc = name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(new RegExp(`:${esc}:`, "gi"), sym);
  }
  return out;
};

const applyZulipEmojiShortcodesToTextNodes = (root: HTMLElement) => {
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      const t = node as Text;
      t.data = replaceZulipEmojiShortcodes(t.data);
      return;
    }
    node.childNodes.forEach(walk);
  };
  walk(root);
};

/** Wrap selection (or insert empty pair) with Zulip markdown delimiters */
const wrapSelectionWithMarkdown = (
  full: string,
  selStart: number,
  selEnd: number,
  before: string,
  after: string
): { next: string; caret: number } => {
  const a = Math.min(selStart, selEnd);
  const b = Math.max(selStart, selEnd);
  const selected = full.slice(a, b);
  if (selected.length > 0) {
    const inserted = `${before}${selected}${after}`;
    const next = full.slice(0, a) + inserted + full.slice(b);
    return { next, caret: a + inserted.length };
  }
  const inserted = `${before}${after}`;
  const next = full.slice(0, a) + inserted + full.slice(b);
  return { next, caret: a + before.length };
};

/** Full lines covering [lo, hi) character range (for list / indent) */
const expandLineBlockBounds = (full: string, lo: number, hi: number) => {
  const a = Math.min(lo, hi);
  const b = Math.max(lo, hi);
  const start = full.lastIndexOf("\n", a - 1) + 1;
  const nextNl = full.indexOf("\n", Math.max(b - 1, a));
  const end = nextNl === -1 ? full.length : nextNl;
  return { start, end };
};

const mapLinesInSelection = (
  full: string,
  selStart: number,
  selEnd: number,
  mapLine: (line: string, index: number) => string
): { next: string; caret: number } => {
  const { start, end } = expandLineBlockBounds(full, selStart, selEnd);
  const block = full.slice(start, end);
  const lines = block.split("\n");
  const nextBlock = lines.map((line, i) => mapLine(line, i)).join("\n");
  const next = full.slice(0, start) + nextBlock + full.slice(end);
  return { next, caret: start + nextBlock.length };
};

const toneClassMap: Record<ChatMessage["tone"], string> = {
  violet: "bg-violet-500",
  green: "bg-emerald-500",
  orange: "bg-orange-500",
};

const initialsFromName = (name: string) =>
  name
    .split(" ")
    .map(part => part[0] ?? "")
    .join("")
    .slice(0, 2)
    .toUpperCase();

const normalizeZulipContent = (content: string) => {
  if (!content) {
    return "";
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(content, "text/html");
    return doc.body.textContent?.trim() ?? content;
  } catch {
    return content;
  }
};

/** Plain text for clipboard — never raw HTML or Zulip markdown. */
const plainTextFromMessageForClipboard = (message: ChatMessage): string => {
  let raw = (message.text ?? "").trim();
  if (!raw && message.htmlContent?.trim()) {
    raw = normalizeZulipContent(message.htmlContent);
  }
  if (!raw) {
    return "";
  }
  if (/<[a-z][\s\S]*>/i.test(raw)) {
    raw = normalizeZulipContent(raw).trim();
  }
  return stripZulipMarkdownToPlain(raw);
};

const stripZulipMarkdownToPlain = (s: string) =>
  s
    .replace(/\*\*([^*]+)\*\*/g, "$1")
    .replace(/\*([^*]+)\*/g, "$1")
    .replace(/__([^_]+)__/g, "$1")
    .replace(/~~([^~]+)~~/g, "$1")
    .replace(/`([^`]+)`/g, "$1")
    .replace(/\[([^\]]+)\]\([^)\s]+\)/g, "$1")
    .replace(/^#{1,6}\s+/gm, "")
    .replace(/^\s*[-*+]\s+/gm, "")
    .replace(/^\s*\d+\.\s+/gm, "")
    .trim();

const formatRelativeDayLabel = (timestamp: number) => {
  const messageDate = new Date(timestamp);
  const now = new Date();
  const startOfToday = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate()
  );
  const startOfMessageDay = new Date(
    messageDate.getFullYear(),
    messageDate.getMonth(),
    messageDate.getDate()
  );
  const dayDiff = Math.round(
    (startOfToday.getTime() - startOfMessageDay.getTime()) /
      (1000 * 60 * 60 * 24)
  );
  if (dayDiff === 0) {
    return "Today";
  }
  if (dayDiff === 1) {
    return "Yesterday";
  }
  return messageDate.toLocaleDateString([], {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const escapeHtml = (value: string) =>
  value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");

/** Zulip markdown user/group mentions → HTML (name highlighted via CSS) */
const mentionMarkdownToHtmlSpans = (raw: string) =>
  raw.replace(/@\*\*([^*]+)\*\*/g, (_match, name: string) => {
    const safe = escapeHtml(name.trim());
    return `<span class="zulip-mention-wrap">@<span class="zulip-mention-name">${safe}</span></span>`;
  });

/** Zulip HTML mentions: keep `@` default color, wrap display name in `.zulip-mention-name` */
const refineZulipNativeMentionForRedName = (el: Element) => {
  if (el.querySelector("a,img,code,.zulip-mention-name")) {
    return;
  }
  const raw = (el.textContent ?? "").trim();
  if (!raw) {
    return;
  }
  el.textContent = "";
  if (raw.startsWith("@") && raw.length > 1) {
    el.appendChild(document.createTextNode("@"));
    const nameSpan = document.createElement("span");
    nameSpan.className = "zulip-mention-name";
    nameSpan.textContent = raw.slice(1);
    el.appendChild(nameSpan);
    return;
  }
  const nameSpan = document.createElement("span");
  nameSpan.className = "zulip-mention-name";
  nameSpan.textContent = raw;
  el.appendChild(nameSpan);
};

const resolveZulipRootUrl = () => {
  const rootUrl =
    import.meta.env.VITE_PTS_API_ROOT_URL ??
    import.meta.env.VITE_CITYCOLLAB_API_ROOT_URL;
  if (typeof rootUrl === "string" && rootUrl.trim()) {
    return rootUrl.replace(/\/+$/, "");
  }
  const apiBaseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
  if (typeof apiBaseUrl === "string" && apiBaseUrl.trim()) {
    return apiBaseUrl
      .replace(/\/api\/v\d+\/?$/i, "")
      .replace(/\/api\/?$/i, "")
      .replace(/\/+$/, "");
  }
  return "";
};

/** Safe `@**name**` fragment for Zulip (strip `*` from display name). */
const zulipMentionMarkdownForDisplayName = (displayName: string) =>
  `@**${displayName.replace(/\*/g, "").trim()}**`;

const postZulipStreamTopicMessage = async (
  baseUrl: string,
  authHeader: string,
  streamName: string,
  topic: string,
  content: string
): Promise<boolean> => {
  try {
    const payload = new URLSearchParams();
    payload.set("type", "stream");
    payload.set("to", streamName);
    payload.set("topic", topic);
    payload.set("content", content);
    const response = await fetch(`${baseUrl}/messages`, {
      method: "POST",
      headers: {
        Authorization: authHeader,
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: payload.toString(),
    });
    return response.ok;
  } catch {
    return false;
  }
};

const formatMessageContent = (html: string, authHeader?: string | null) => {
  if (!html) {
    return "";
  }

  const apiBaseUrl = resolveZulipRootUrl();
  if (!apiBaseUrl) {
    const looksLikeHtml = /<[a-z][\s\S]*>/i.test(html);
    const prepared = looksLikeHtml ? html : mentionMarkdownToHtmlSpans(html);
    const div = document.createElement("div");
    div.innerHTML = prepared;
    applyZulipEmojiShortcodesToTextNodes(div);
    if (looksLikeHtml) {
      div
        .querySelectorAll(".user-mention, .user-group-mention")
        .forEach(refineZulipNativeMentionForRedName);
    }
    return div.innerHTML;
  }

  const IMAGE_EXTS = /\.(png|jpe?g|gif|webp|svg)$/i;
  const hasHtmlTags = /<[a-z][\s\S]*>/i.test(html);
  const normalizedHtml = hasHtmlTags
    ? html
    : mentionMarkdownToHtmlSpans(html)
        .replace(
          /\[([^\]]+)\]\(([^)\s]+)\)/g,
          (_match, label: string, rawHref: string) => {
            const safeLabel = escapeHtml(label.trim());
            const safeHref = escapeHtml(rawHref.trim());
            if (IMAGE_EXTS.test(rawHref)) {
              return `<a href="${safeHref}"><img src="${safeHref}" alt="${safeLabel}" /></a>`;
            }
            return `<a href="${safeHref}">${safeLabel}</a>`;
          }
        )
        .replace(
          /(^|\s)(\/user_uploads\/\S+\.(?:png|jpe?g|gif|webp|svg))/gi,
          (_match, prefix: string, path: string) =>
            `${prefix}<img src="${escapeHtml(path)}" alt="uploaded image" />`
        );

  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = normalizedHtml;
  applyZulipEmojiShortcodesToTextNodes(tempDiv);
  if (hasHtmlTags) {
    tempDiv
      .querySelectorAll(".user-mention, .user-group-mention")
      .forEach(refineZulipNativeMentionForRedName);
  }

  let authParams = "";
  if (authHeader?.startsWith("Basic ")) {
    try {
      const decoded = atob(authHeader.replace("Basic ", ""));
      const [email, apiKey] = decoded.split(":");
      if (email && apiKey) {
        authParams = `api_key=${encodeURIComponent(apiKey)}&email=${encodeURIComponent(email)}`;
      }
    } catch (error) {
      console.error("Error decoding zulip auth header", error);
    }
  }

  const appendAuthParams = (url: string) => {
    if (!authParams) {
      return url;
    }
    if (/[?&]api_key=/.test(url) || /[?&]email=/.test(url)) {
      return url;
    }
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}${authParams}`;
  };

  const links = tempDiv.querySelectorAll("a");
  links.forEach(link => {
    const href = link.getAttribute("href");
    if (href && (href.startsWith("/") || href.includes("/user_uploads/"))) {
      const fullUrl = href.startsWith("/") ? `${apiBaseUrl}${href}` : href;
      link.setAttribute("href", appendAuthParams(fullUrl));
      link.setAttribute("target", "_blank");
      link.setAttribute("rel", "noopener noreferrer");
    }

    const linkText = link.textContent?.trim() ?? "";
    const isUploadLink = Boolean(
      href && (href.includes("/user_uploads/") || href.startsWith("/"))
    );
    if (!isUploadLink) {
      return;
    }

    const containsImg = link.querySelector("img") !== null;
    if (containsImg) {
      while (link.firstChild) {
        link.parentNode?.insertBefore(link.firstChild, link);
      }
      link.remove();
    } else if (IMAGE_EXTS.test(linkText)) {
      link.remove();
    }
  });

  const images = tempDiv.querySelectorAll("img");
  images.forEach(img => {
    const src = img.getAttribute("src");
    if (src && (src.startsWith("/") || src.includes("/user_uploads/"))) {
      const fullUrl = src.startsWith("/") ? `${apiBaseUrl}${src}` : src;
      img.setAttribute("src", appendAuthParams(fullUrl));
      img.setAttribute("loading", "lazy");
      img.setAttribute("decoding", "async");
      img.setAttribute(
        "style",
        [
          "display:block",
          "max-width:100%",
          "width:auto",
          "max-height:320px",
          "height:auto",
          "object-fit:contain",
          "border:1px solid #e2e8f0",
          "border-radius:12px",
          "margin-top:6px",
          "margin-bottom:6px",
          "background:#f8fafc",
        ].join(";")
      );
    }
  });

  return tempDiv.innerHTML;
};

const aggregateZulipReactions = (
  reactions: ZulipMessage["reactions"],
  currentUserId: number | null
): { emoji: string; count: number; reactedByMe?: boolean }[] => {
  if (!Array.isArray(reactions) || reactions.length === 0) {
    return [];
  }
  const counts = new Map<string, { count: number; reactedByMe: boolean }>();
  for (const reaction of reactions) {
    const emojiName = reaction.emoji_name;
    if (!emojiName) {
      continue;
    }
    const symbol = SYMBOL_BY_EMOJI_NAME[emojiName] ?? "🙂";
    const existing = counts.get(symbol) ?? { count: 0, reactedByMe: false };
    counts.set(symbol, {
      count: existing.count + 1,
      reactedByMe:
        existing.reactedByMe ||
        (currentUserId !== null && reaction.user_id === currentUserId),
    });
  }
  return Array.from(counts.entries()).map(([emoji, meta]) => ({
    emoji,
    count: meta.count,
    reactedByMe: meta.reactedByMe,
  }));
};

const limitReactionsByConversation = (
  conversationId: string,
  reactions: { emoji: string; count: number; reactedByMe?: boolean }[]
) => {
  if (!conversationId.startsWith("dm-")) {
    return reactions;
  }
  if (reactions.length === 0) {
    return reactions;
  }
  const [first] = reactions;
  return [{ emoji: first.emoji, count: 1, reactedByMe: first.reactedByMe }];
};

const resolveFreshPresenceStatus = (
  clientPresence?: Record<string, ZulipPresenceEntry>
): "active" | "idle" | "offline" => {
  if (!clientPresence) {
    return "offline";
  }
  // Zulip docs/replies expose status in aggregated first.
  const aggregatedStatus = clientPresence.aggregated?.status;
  if (
    aggregatedStatus === "active" ||
    aggregatedStatus === "idle" ||
    aggregatedStatus === "offline"
  ) {
    return aggregatedStatus;
  }

  const websiteStatus = clientPresence.website?.status;
  if (
    websiteStatus === "active" ||
    websiteStatus === "idle" ||
    websiteStatus === "offline"
  ) {
    return websiteStatus;
  }

  const entries = Object.values(clientPresence);
  const newest = entries.reduce<ZulipPresenceEntry | null>(
    (latest, current) => {
      if (!latest) {
        return current;
      }
      return (current.timestamp ?? 0) > (latest.timestamp ?? 0)
        ? current
        : latest;
    },
    null
  );
  if (
    newest?.status === "active" ||
    newest?.status === "idle" ||
    newest?.status === "offline"
  ) {
    return newest.status;
  }
  return "offline";
};

/** Plain Zulip `message.text` (no HTML): `@` stays default; name inside `@**…**` is highlighted */
const ZulipPlainMessageText = ({ text }: { text: string }) => {
  const re = /@\*\*([^*]+)\*\*/g;
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let match: RegExpExecArray | null;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) {
      nodes.push(replaceZulipEmojiShortcodes(text.slice(last, match.index)));
    }
    const name = match[1].trim();
    nodes.push(
      <Fragment key={`m-${match.index}`}>
        @<span className="zulip-mention-name">{name}</span>
      </Fragment>
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) {
    nodes.push(replaceZulipEmojiShortcodes(text.slice(last)));
  }
  if (nodes.length === 0) {
    return replaceZulipEmojiShortcodes(text);
  }
  return nodes;
};

/** Timestamp line embedded in ```quote (first line) for reply UI */
const formatQuotedSourceTimestamp = (
  sortKey?: number,
  timeLabel?: string
): string => {
  if (typeof sortKey === "number" && sortKey > 0) {
    const d = new Date(sortKey);
    const pad = (n: number) => String(n).padStart(2, "0");
    const date = `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()}`;
    const time = d.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
    return `${date} ${time}`;
  }
  return (timeLabel ?? "").trim();
};

/** Split `@**Name**:\n```quote\n…\n```\n\nreply` from composer / Zulip markdown */
type ParsedQuoteReply = {
  quotedAuthor: string;
  quotedMetaLine: string;
  quotedBody: string;
  replyMarkdown: string;
};

const parseZulipQuoteReplyFromText = (
  fullText: string
): ParsedQuoteReply | null => {
  const text = fullText.trim();
  const re =
    /^@\*\*([^*]+)\*\*:\s*(?:\r?\n)?```\s*quote\s*(?:\r?\n)([\s\S]*?)```(?:\s*(?:\r?\n))+([\s\S]*)$/im;
  const m = text.match(re);
  if (!m) {
    return null;
  }
  const quotedAuthor = m[1].trim();
  const inner = m[2].trim();
  const replyMarkdown = (m[3] ?? "").trim();
  const innerLines = inner.split(/\r?\n/);
  let quotedMetaLine = "";
  let quotedBody = inner;
  if (innerLines.length >= 2) {
    const first = innerLines[0].trim();
    if (
      /^\d{1,2}[-/]\d{1,2}[-/]\d{2,4}/.test(first) ||
      /\d{1,2}:\d{2}/.test(first)
    ) {
      quotedMetaLine = first;
      quotedBody = innerLines.slice(1).join("\n").trim();
    }
  }
  return { quotedAuthor, quotedMetaLine, quotedBody, replyMarkdown };
};

const ZulipQuoteReplyBubble = ({
  message,
  parsed,
  authHeader,
  onImageClick,
}: {
  message: ChatMessage;
  parsed: ParsedQuoteReply;
  authHeader: string | null;
  onImageClick: (e: MouseEvent<HTMLDivElement>) => void;
}) => {
  const quoteHtml = formatMessageContent(
    parsed.quotedBody.trim() || " ",
    authHeader ?? undefined
  );
  const replyHtml = formatMessageContent(
    parsed.replyMarkdown.trim() || "\u00a0",
    authHeader ?? undefined
  );
  const replyShellClass =
    "rounded-2xl border border-slate-200/70 bg-slate-50/90 px-3 py-2 text-slate-700";
  return (
    <div
      className={`pts-zulip-reply-bubble max-w-full text-left text-sm leading-relaxed [overflow-wrap:anywhere] ${replyShellClass}`}
      role="presentation"
      data-preview-sender={message.author}
      data-preview-time={message.timeLabel}
      data-preview-message-id={message.id}
      data-preview-sort-key={String(message.sortKey ?? Date.now())}
      onClick={onImageClick}
    >
      <div className="rounded-lg border border-slate-200/90 bg-white py-2 pl-3 pr-3 shadow-sm [border-left-color:#94a3b8] [border-left-width:4px]">
        <div className="mb-1.5 flex flex-wrap items-baseline justify-between gap-x-2 gap-y-0.5">
          <span className="text-sm font-semibold text-slate-900">
            {parsed.quotedAuthor}
          </span>
          {parsed.quotedMetaLine ? (
            <span className="text-[11px] font-medium tabular-nums text-slate-500">
              {parsed.quotedMetaLine}
            </span>
          ) : null}
        </div>
        <div
          className="zulip-message-content text-[13px] leading-relaxed text-slate-700"
          dangerouslySetInnerHTML={{ __html: quoteHtml }}
        />
      </div>
      {parsed.replyMarkdown.trim() ? (
        <div
          className="zulip-message-content mt-2.5 text-sm font-medium leading-relaxed text-slate-900"
          dangerouslySetInnerHTML={{ __html: replyHtml }}
        />
      ) : null}
    </div>
  );
};

const ChatPage = () => {
  const ptsChatMessageEditEnabled = viteEnvBool(
    import.meta.env.VITE_PTS_CHAT_MESSAGE_EDIT_ENABLED
  );
  const ptsChatMessageDeleteEnabled = viteEnvBool(
    import.meta.env.VITE_PTS_CHAT_MESSAGE_DELETE_ENABLED
  );
  const {
    unreadByConversationId,
    setActiveChatConversationId,
    clearConversationUnread,
    bumpUnread,
  } = usePtsZulipUnread();
  const [activeConversationId, setActiveConversationId] = useState("");
  const [activeTopic, setActiveTopic] = useState("standup");
  const [messageInput, setMessageInput] = useState("");
  /** Reply draft: auto-prefixed Zulip quote block length + label for composer strip */
  const [composerReplyStrip, setComposerReplyStrip] = useState<{
    prefixLen: number;
    replyToLabel: string;
  } | null>(null);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [showComposerFormattingToolbar, setShowComposerFormattingToolbar] =
    useState(false);
  const [reactionPickerForMessage, setReactionPickerForMessage] = useState<
    string | null
  >(null);
  const [streamChannels, setStreamChannels] = useState<Conversation[]>([]);
  const [dmUsers, setDmUsers] = useState<Conversation[]>([]);
  const [zulipEmailByUserId, setZulipEmailByUserId] = useState<
    Record<number, string>
  >({});
  const [presenceByUserId, setPresenceByUserId] = useState<
    Record<number, "active" | "idle" | "offline">
  >({});
  const [zulipBasicAuthHeader, setZulipBasicAuthHeader] = useState<
    string | null
  >(null);
  const [zulipUserId, setZulipUserId] = useState<number | null>(null);
  const [remoteMessagesByConversation, setRemoteMessagesByConversation] =
    useState<Record<string, ChatMessage[]>>({});
  const [sendingDmMessage, setSendingDmMessage] = useState(false);
  const [topicsByChannel, setTopicsByChannel] = useState<
    Record<string, string[]>
  >({});
  const [newTopicName, setNewTopicName] = useState("");
  const [creatingTopic, setCreatingTopic] = useState(false);
  const [showCreateChannel, setShowCreateChannel] = useState(false);
  const [newChannelName, setNewChannelName] = useState("");
  const [newChannelInviteOnly, setNewChannelInviteOnly] = useState(false);
  const [newChannelMemberIds, setNewChannelMemberIds] = useState<number[]>([]);
  const [newChannelMemberSearch, setNewChannelMemberSearch] = useState("");
  const [creatingChannel, setCreatingChannel] = useState(false);
  const [showMembersModal, setShowMembersModal] = useState(false);
  const [memberSearchText, setMemberSearchText] = useState("");
  const [conversationSearchText, setConversationSearchText] = useState("");
  const [sidebarChannelsOpen, setSidebarChannelsOpen] = useState(true);
  const [sidebarDirectMessagesOpen, setSidebarDirectMessagesOpen] =
    useState(true);
  /** Newly created / noteworthy channel row in sidebar until opened or timeout */
  const [sidebarChannelSpotlightId, setSidebarChannelSpotlightId] = useState<
    string | null
  >(null);
  const [mentionQuery, setMentionQuery] = useState("");
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(
    null
  );
  const [activeMentionIndex, setActiveMentionIndex] = useState(0);
  const [showChannelMembersModal, setShowChannelMembersModal] = useState(false);
  const [channelMemberSearchText, setChannelMemberSearchText] = useState("");
  const [channelMembersByConversationId, setChannelMembersByConversationId] =
    useState<Record<string, number[]>>({});
  const [loadingChannelMembers, setLoadingChannelMembers] = useState(false);
  const [updatingChannelMemberUserId, setUpdatingChannelMemberUserId] =
    useState<number | null>(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [editingMessageId, setEditingMessageId] = useState<string | null>(null);
  const [editingMessageSenderZulipId, setEditingMessageSenderZulipId] =
    useState<number | null>(null);
  const [editingMessageDraft, setEditingMessageDraft] = useState("");
  const [messageEditError, setMessageEditError] = useState<string | null>(null);
  const [messageMutatingId, setMessageMutatingId] = useState<string | null>(
    null
  );
  const [messageActionsMenuOpenId, setMessageActionsMenuOpenId] = useState<
    string | null
  >(null);
  const [deleteConfirmMessageId, setDeleteConfirmMessageId] = useState<
    string | null
  >(null);
  const [deleteConfirmSenderZulipId, setDeleteConfirmSenderZulipId] = useState<
    number | null
  >(null);
  const [chatImagePreview, setChatImagePreview] = useState<{
    src: string;
    alt: string;
    senderName: string;
    timeLabel: string;
    messageId: string;
    sortKey: number;
  } | null>(null);
  const [chatImagePreviewZoom, setChatImagePreviewZoom] = useState(1);
  const [chatImageDownloadBusy, setChatImageDownloadBusy] = useState(false);
  const eventsStopRef = useRef(false);
  const eventsSessionRef = useRef(0);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const messageInputRef = useRef<HTMLTextAreaElement | null>(null);
  const messageEditTextareaRef = useRef<HTMLTextAreaElement | null>(null);
  const chatMessagesScrollRef = useRef<HTMLDivElement | null>(null);

  const fetchStreamsRef = useRef<
    | ((basicAuthHeader: string, baseUrl: string) => Promise<Conversation[]>)
    | null
  >(null);

  const fetchStreams = async (basicAuthHeader: string, baseUrl: string) => {
    try {
      const streamsResponse = await fetch(`${baseUrl}/streams`, {
        method: "GET",
        headers: { Authorization: basicAuthHeader },
      });
      if (!streamsResponse.ok) {
        return [];
      }

      const streamsData: ZulipStreamsResponse = await streamsResponse.json();
      if (!Array.isArray(streamsData?.streams)) {
        return [];
      }

      const mappedChannels: Conversation[] = streamsData.streams.map(
        stream => ({
          id: `ch-${stream.stream_id}`,
          name: stream.name,
          type: "channel",
          unread: 0,
          isPrivate: !!stream.invite_only,
          streamId: stream.stream_id,
        })
      );
      setStreamChannels(mappedChannels);
      return mappedChannels;
    } catch (error) {
      console.warn("Failed to fetch Zulip streams:", error);
      return [];
    }
  };

  fetchStreamsRef.current = fetchStreams;

  /** When GET /streams lags after create, resolve id from the user's subscriptions. */
  const fetchChannelFromMySubscriptions = async (
    streamName: string,
    authHeader: string,
    apiBase: string
  ): Promise<Conversation | null> => {
    try {
      const res = await fetch(
        `${apiBase}/users/me/subscriptions?include_subscribers=false`,
        { headers: { Authorization: authHeader } }
      );
      if (!res.ok) {
        return null;
      }
      const data = (await res.json()) as ZulipSubscriptionsResponse;
      const subs = data.subscriptions ?? [];
      const lc = streamName.toLowerCase();
      const hit = subs.find(s => s.name.toLowerCase() === lc);
      if (!hit || typeof hit.stream_id !== "number") {
        return null;
      }
      return {
        id: `ch-${hit.stream_id}`,
        name: hit.name,
        type: "channel",
        unread: 0,
        isPrivate: Boolean(hit.invite_only),
        streamId: hit.stream_id,
      };
    } catch {
      return null;
    }
  };

  useEffect(() => {
    const fetchStreamsAndUsers = async () => {
      const rawZulipAuth = localStorage.getItem("zulipAuth");
      if (!rawZulipAuth) {
        return;
      }

      let parsed: unknown = null;
      try {
        parsed = JSON.parse(rawZulipAuth);
      } catch {
        parsed = rawZulipAuth;
      }

      const basicAuthHeader = toBasicHeader(parsed);
      if (!basicAuthHeader) {
        return;
      }
      setZulipBasicAuthHeader(basicAuthHeader);
      setZulipUserId(toZulipUserId(parsed));

      const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
      if (!baseUrl) {
        return;
      }

      try {
        const [mappedChannels, usersResponse] = await Promise.all([
          fetchStreams(basicAuthHeader, baseUrl),
          fetch(`${baseUrl}/users`, {
            method: "GET",
            headers: { Authorization: basicAuthHeader },
          }),
        ]);
        void mappedChannels;

        if (usersResponse.ok) {
          const usersData: ZulipUsersResponse = await usersResponse.json();
          const members = usersData.members ?? usersData.users ?? [];
          setZulipEmailByUserId(
            members.reduce(
              (acc, member) => {
                const email = member.email ?? member.delivery_email;
                if (email) {
                  acc[member.user_id] = email;
                }
                return acc;
              },
              {} as Record<number, string>
            )
          );
          const mappedUsers: Conversation[] = members
            .filter(member => member.is_active && !member.is_bot)
            .map(member => ({
              id: `dm-${member.user_id}`,
              name: member.full_name,
              type: "dm",
              unread: 0,
              zulipUserId: member.user_id,
            }));
          if (mappedUsers.length > 0) {
            setDmUsers(mappedUsers);
          }
        }
      } catch (error) {
        console.warn("Failed to fetch Zulip streams/users:", error);
      }
    };

    fetchStreamsAndUsers();
  }, []);

  const channels = useMemo(() => {
    const getLatestSortKey = (conversationId: string) => {
      const conversationMessages =
        remoteMessagesByConversation[conversationId] ?? [];
      if (conversationMessages.length === 0) {
        return 0;
      }
      return Math.max(
        ...conversationMessages.map(message => message.sortKey ?? 0)
      );
    };

    return [...streamChannels].sort(
      (a, b) => getLatestSortKey(b.id) - getLatestSortKey(a.id)
    );
  }, [streamChannels, remoteMessagesByConversation]);
  const directMessages = useMemo(() => {
    const visibleUsers = dmUsers.filter(user => {
      const hasUnread = (unreadByConversationId[user.id] ?? 0) > 0;
      const hasHistory =
        (remoteMessagesByConversation[user.id]?.length ?? 0) > 0;
      const isActiveDm =
        user.id === activeConversationId &&
        activeConversationId.startsWith("dm-");
      return hasUnread || hasHistory || isActiveDm;
    });
    // Ensure the same member appears only once in the list.
    const uniqueByUserId = new Map<string, Conversation>();
    for (const user of visibleUsers) {
      const key = user.zulipUserId ? String(user.zulipUserId) : user.id;
      if (!uniqueByUserId.has(key)) {
        uniqueByUserId.set(key, user);
      }
    }
    const uniqueUsers = Array.from(uniqueByUserId.values());

    const getLatestSortKey = (conversationId: string) => {
      const conversationMessages =
        remoteMessagesByConversation[conversationId] ?? [];
      if (conversationMessages.length === 0) {
        return 0;
      }
      return Math.max(
        ...conversationMessages.map(message => message.sortKey ?? 0)
      );
    };

    return uniqueUsers.sort(
      (a, b) => getLatestSortKey(b.id) - getLatestSortKey(a.id)
    );
  }, [
    dmUsers,
    unreadByConversationId,
    remoteMessagesByConversation,
    activeConversationId,
  ]);
  const membersModalUsers = useMemo(() => {
    const term = memberSearchText.trim().toLowerCase();
    if (!term) {
      return dmUsers;
    }
    return dmUsers.filter(user => user.name.toLowerCase().includes(term));
  }, [dmUsers, memberSearchText]);
  const channelMemberCandidates = useMemo(() => {
    const term = channelMemberSearchText.trim().toLowerCase();
    const sorted = [...dmUsers].sort((a, b) => a.name.localeCompare(b.name));
    if (!term) {
      return sorted;
    }
    return sorted.filter(user => user.name.toLowerCase().includes(term));
  }, [dmUsers, channelMemberSearchText]);
  const createChannelMemberCandidates = useMemo(() => {
    const term = newChannelMemberSearch.trim().toLowerCase();
    const sorted = [...dmUsers].sort((a, b) => a.name.localeCompare(b.name));
    if (!term) {
      return sorted;
    }
    return sorted.filter(user => user.name.toLowerCase().includes(term));
  }, [dmUsers, newChannelMemberSearch]);
  const activeChannelMemberIds = useMemo(() => {
    return new Set(channelMembersByConversationId[activeConversationId] ?? []);
  }, [channelMembersByConversationId, activeConversationId]);
  const usersInActiveChannel = useMemo(
    () =>
      channelMemberCandidates.filter(user =>
        activeChannelMemberIds.has(user.zulipUserId ?? -1)
      ),
    [channelMemberCandidates, activeChannelMemberIds]
  );
  const usersOutsideActiveChannel = useMemo(
    () =>
      channelMemberCandidates.filter(
        user => !activeChannelMemberIds.has(user.zulipUserId ?? -1)
      ),
    [channelMemberCandidates, activeChannelMemberIds]
  );
  const userIdByLowerEmail = useMemo(() => {
    const map: Record<string, number> = {};
    for (const [userIdText, email] of Object.entries(zulipEmailByUserId)) {
      map[email.toLowerCase()] = Number(userIdText);
    }
    return map;
  }, [zulipEmailByUserId]);
  const conversations = useMemo(
    () => [...channels, ...directMessages],
    [channels, directMessages]
  );
  const filteredChannels = useMemo(() => {
    const term = conversationSearchText.trim().toLowerCase();
    if (!term) {
      return channels;
    }
    return channels.filter(channel =>
      channel.name.toLowerCase().includes(term)
    );
  }, [channels, conversationSearchText]);
  const filteredDirectMessages = useMemo(() => {
    const term = conversationSearchText.trim().toLowerCase();
    if (!term) {
      return directMessages;
    }
    return directMessages.filter(dm => dm.name.toLowerCase().includes(term));
  }, [directMessages, conversationSearchText]);

  useEffect(() => {
    if (!conversations.some(item => item.id === activeConversationId)) {
      const firstAvailable = conversations[0]?.id ?? "";
      if (firstAvailable) {
        setActiveConversationId(firstAvailable);
      }
    }
  }, [activeConversationId, conversations]);

  useEffect(() => {
    if (!showCreateChannel || zulipUserId === null) {
      return;
    }
    setNewChannelMemberIds(prev =>
      prev.includes(zulipUserId) ? prev : [...prev, zulipUserId]
    );
  }, [showCreateChannel, zulipUserId]);

  useEffect(() => {
    if (!sidebarChannelSpotlightId) {
      return;
    }
    const timer = window.setTimeout(() => {
      setSidebarChannelSpotlightId(null);
    }, 60000);
    return () => window.clearTimeout(timer);
  }, [sidebarChannelSpotlightId]);

  /** Refetch channels when returning to the tab (covers missed subscription events). */
  useEffect(() => {
    if (!zulipBasicAuthHeader) {
      return;
    }
    const bu = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!bu) {
      return;
    }
    const onVisible = () => {
      if (document.visibilityState !== "visible") {
        return;
      }
      const fn = fetchStreamsRef.current;
      if (fn) {
        void fn(zulipBasicAuthHeader, bu);
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [zulipBasicAuthHeader]);

  const activeConversation = useMemo(
    () => conversations.find(item => item.id === activeConversationId) ?? null,
    [activeConversationId, conversations]
  );
  const mentionOptions = useMemo(() => {
    const sortedUsers = [...dmUsers].sort((a, b) =>
      a.name.localeCompare(b.name)
    );
    const channelUsers =
      activeChannelMemberIds.size > 0
        ? sortedUsers.filter(user =>
            activeChannelMemberIds.has(user.zulipUserId ?? -1)
          )
        : sortedUsers;
    const userOptions: MentionOption[] = channelUsers.map(user => ({
      id: `user-${user.zulipUserId ?? user.id}`,
      label: user.name,
      mentionText: `@**${user.name}**`,
    }));

    if (activeConversation?.type !== "channel") {
      return userOptions;
    }

    return [
      { id: "special-all", label: "all", mentionText: "@**all**" },
      { id: "special-channel", label: "channel", mentionText: "@**channel**" },
      ...userOptions,
    ];
  }, [activeChannelMemberIds, activeConversation?.type, dmUsers]);
  const filteredMentionOptions = useMemo(() => {
    const normalized = mentionQuery.trim().toLowerCase();
    if (!normalized) {
      return mentionOptions;
    }
    return mentionOptions.filter(option =>
      option.label.toLowerCase().includes(normalized)
    );
  }, [mentionOptions, mentionQuery]);
  const showMentionSuggestions =
    mentionStartIndex !== null && filteredMentionOptions.length > 0;
  const topicsForActiveConversation = topicsByChannel[activeConversationId] ?? [
    "general",
    "standup",
    "maintenance",
  ];

  const activeMessages = useMemo(
    () => messages.filter(item => item.conversationId === activeConversationId),
    [messages, activeConversationId]
  );
  const displayMessages =
    remoteMessagesByConversation[activeConversationId] ?? activeMessages;
  const messagesWithDateDividers = useMemo(() => {
    const baseTimestamp = Date.now();
    const rows: Array<
      | { kind: "divider"; id: string; label: string }
      | { kind: "message"; id: string; message: ChatMessage }
    > = [];
    let previousLabel: string | null = null;

    displayMessages.forEach((message, index) => {
      const timestamp = message.sortKey ?? baseTimestamp + index;
      const currentLabel = formatRelativeDayLabel(timestamp);

      if (currentLabel !== previousLabel) {
        rows.push({
          kind: "divider",
          id: `date-divider-${currentLabel}-${timestamp}`,
          label: currentLabel,
        });
        previousLabel = currentLabel;
      }

      rows.push({
        kind: "message",
        id: message.id,
        message,
      });
    });

    return rows;
  }, [displayMessages]);
  const lastDisplayMessageId = useMemo(() => {
    if (displayMessages.length === 0) {
      return "";
    }
    return displayMessages[displayMessages.length - 1]?.id ?? "";
  }, [displayMessages]);

  const scrollChatMessagesToBottom = useCallback(() => {
    const el = chatMessagesScrollRef.current;
    if (!el) {
      return;
    }
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        el.scrollTop = el.scrollHeight;
      });
    });
  }, []);

  useEffect(() => {
    scrollChatMessagesToBottom();
  }, [
    activeConversationId,
    activeTopic,
    displayMessages.length,
    lastDisplayMessageId,
    scrollChatMessagesToBottom,
  ]);

  const getPresenceStatus = (userId?: number) => {
    if (!userId) {
      return "offline" as const;
    }
    return presenceByUserId[userId] ?? "offline";
  };

  const mapZulipMessages = (items: ZulipMessage[], conversationId: string) => {
    return items.map((item, index) => {
      const isOwn = zulipUserId !== null && item.sender_id === zulipUserId;
      const date = new Date(item.timestamp * 1000);
      const tone: ChatMessage["tone"] = isOwn
        ? "orange"
        : index % 2 === 0
          ? "violet"
          : "green";

      return {
        id: `zulip-${item.id}`,
        conversationId,
        author: isOwn ? "You" : item.sender_full_name,
        authorCode: isOwn
          ? "YO"
          : initialsFromName(item.sender_full_name || "User"),
        tone,
        text: normalizeZulipContent(item.content),
        htmlContent: formatMessageContent(item.content, zulipBasicAuthHeader),
        timeLabel: date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        sortKey: item.timestamp * 1000,
        senderZulipUserId: item.sender_id,
        reactions: limitReactionsByConversation(
          conversationId,
          aggregateZulipReactions(item.reactions, zulipUserId)
        ),
        starredByMe:
          Array.isArray(item.flags) && item.flags.includes("starred"),
      };
    });
  };

  const bootstrapDirectMessages = async () => {
    if (!zulipBasicAuthHeader) {
      return;
    }
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    const params = new URLSearchParams({
      anchor: "newest",
      num_before: "200",
      num_after: "0",
      narrow: JSON.stringify([{ operator: "is", operand: "private" }]),
    });

    try {
      const response = await fetch(`${baseUrl}/messages?${params.toString()}`, {
        method: "GET",
        headers: {
          Authorization: zulipBasicAuthHeader,
        },
      });
      if (!response.ok) {
        return;
      }

      const data: ZulipMessagesResponse = await response.json();
      if (!Array.isArray(data.messages)) {
        return;
      }

      const groupedByUser = new Map<number, ZulipMessage[]>();
      const userNames = new Map<number, string>();

      for (const msg of data.messages) {
        const recipients = Array.isArray(msg.display_recipient)
          ? msg.display_recipient
          : [];
        if (recipients.length === 0) {
          continue;
        }
        const other = recipients.find(
          recipient => recipient.id !== zulipUserId
        );
        if (!other) {
          continue;
        }

        if (!groupedByUser.has(other.id)) {
          groupedByUser.set(other.id, []);
        }
        groupedByUser.get(other.id)?.push(msg);
        userNames.set(other.id, other.full_name);
      }

      if (groupedByUser.size === 0) {
        return;
      }

      setDmUsers(prev => {
        const byId = new Map(prev.map(user => [user.id, user] as const));
        for (const [userId, fullName] of userNames.entries()) {
          const id = `dm-${userId}`;
          if (!byId.has(id)) {
            byId.set(id, {
              id,
              name: fullName,
              type: "dm",
              unread: 0,
              zulipUserId: userId,
            });
          }
        }
        return Array.from(byId.values());
      });

      setRemoteMessagesByConversation(prev => {
        const next = { ...prev };
        for (const [userId, messagesForUser] of groupedByUser.entries()) {
          const conversationId = `dm-${userId}`;
          next[conversationId] = mapZulipMessages(
            messagesForUser,
            conversationId
          );
        }
        return next;
      });
    } catch (error) {
      console.warn("Failed to bootstrap direct messages:", error);
    }
  };

  const mapSingleZulipMessage = useCallback(
    (item: ZulipMessage, conversationId: string): ChatMessage => {
      const isOwn = zulipUserId !== null && item.sender_id === zulipUserId;
      const date = new Date(item.timestamp * 1000);
      return {
        id: `zulip-${item.id}`,
        conversationId,
        author: isOwn ? "You" : item.sender_full_name,
        authorCode: isOwn
          ? "YO"
          : initialsFromName(item.sender_full_name || "User"),
        tone: isOwn ? "orange" : "violet",
        text: normalizeZulipContent(item.content),
        htmlContent: formatMessageContent(item.content, zulipBasicAuthHeader),
        timeLabel: date.toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit",
        }),
        sortKey: item.timestamp * 1000,
        senderZulipUserId: item.sender_id,
        reactions: limitReactionsByConversation(
          conversationId,
          aggregateZulipReactions(item.reactions, zulipUserId)
        ),
        starredByMe:
          Array.isArray(item.flags) && item.flags.includes("starred"),
      };
    },
    [zulipBasicAuthHeader, zulipUserId]
  );

  const resolveConversationIdFromEventMessage = useCallback(
    (message: NonNullable<ZulipEvent["message"]>): string | null => {
      if (message.type === "stream") {
        const byStreamId =
          typeof message.stream_id === "number"
            ? channels.find(channel => channel.streamId === message.stream_id)
            : null;
        if (byStreamId) {
          return byStreamId.id;
        }
        const recipientName =
          typeof message.display_recipient === "string"
            ? message.display_recipient
            : null;
        if (recipientName) {
          const byName = channels.find(
            channel =>
              channel.name.toLowerCase() === recipientName.toLowerCase()
          );
          return byName?.id ?? null;
        }
        return null;
      }

      const recipients = Array.isArray(message.display_recipient)
        ? message.display_recipient
        : [];
      const otherRecipient = recipients.find(
        recipient => recipient.id !== zulipUserId
      );
      const targetUserId =
        otherRecipient?.id ??
        (message.sender_id !== zulipUserId ? message.sender_id : null);
      if (!targetUserId) {
        return null;
      }

      const dmConversationId = `dm-${targetUserId}`;
      const fallbackName =
        otherRecipient?.full_name ?? message.sender_full_name;
      setDmUsers(prev => {
        if (prev.some(user => user.id === dmConversationId)) {
          return prev;
        }
        return [
          ...prev,
          {
            id: dmConversationId,
            name: fallbackName,
            type: "dm",
            unread: 0,
            zulipUserId: targetUserId,
          },
        ];
      });

      return dmConversationId;
    },
    [channels, zulipUserId]
  );

  const fetchConversationMessages = async (
    conversation: Conversation | null
  ) => {
    if (!conversation || !zulipBasicAuthHeader) {
      return;
    }
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    const buildBaseParams = () =>
      new URLSearchParams({
        anchor: "newest",
        num_before: "200",
        num_after: "0",
      });

    try {
      const attempts: URLSearchParams[] = [];
      if (conversation.type === "dm" && conversation.zulipUserId) {
        const dmParams = buildBaseParams();
        dmParams.set(
          "narrow",
          JSON.stringify([
            { operator: "dm", operand: String(conversation.zulipUserId) },
          ])
        );
        attempts.push(dmParams);
      } else if (conversation.type === "channel") {
        const channelParams = buildBaseParams();
        const narrow: Array<{ operator: string; operand: string }> = [
          { operator: "stream", operand: conversation.name },
        ];
        if (activeTopic) {
          narrow.push({ operator: "topic", operand: activeTopic });
        }
        channelParams.set("narrow", JSON.stringify(narrow));
        attempts.push(channelParams);
      } else {
        attempts.push(buildBaseParams());
      }

      let data: ZulipMessagesResponse | null = null;
      for (const params of attempts) {
        const response = await fetch(
          `${baseUrl}/messages?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Authorization: zulipBasicAuthHeader,
            },
          }
        );
        if (!response.ok) {
          continue;
        }
        data = (await response.json()) as ZulipMessagesResponse;
        break;
      }
      if (!data) {
        return;
      }
      if (!Array.isArray(data.messages)) {
        return;
      }

      setRemoteMessagesByConversation(prev => ({
        ...prev,
        [conversation.id]: mapZulipMessages(data.messages, conversation.id),
      }));
    } catch (error) {
      console.warn("Failed to fetch Zulip messages:", error);
    }
  };

  const fetchChannelTopics = async (conversation: Conversation | null) => {
    if (
      !conversation ||
      conversation.type !== "channel" ||
      !conversation.streamId ||
      !zulipBasicAuthHeader
    ) {
      return;
    }

    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    try {
      const response = await fetch(
        `${baseUrl}/users/me/${conversation.streamId}/topics`,
        {
          method: "GET",
          headers: { Authorization: zulipBasicAuthHeader },
        }
      );
      if (!response.ok) {
        return;
      }

      const data: ZulipTopicsResponse = await response.json();
      if (!Array.isArray(data.topics)) {
        return;
      }

      const topicNames = data.topics
        .map(topic => topic.name?.trim())
        .filter((topic): topic is string => Boolean(topic));
      if (topicNames.length > 0) {
        setTopicsByChannel(prev => ({
          ...prev,
          [conversation.id]: Array.from(new Set(topicNames)),
        }));
        if (!topicNames.includes(activeTopic)) {
          setActiveTopic(topicNames[0]);
        }
      }
    } catch (error) {
      console.warn("Failed to fetch Zulip topics:", error);
    }
  };

  useEffect(() => {
    if (activeConversation) {
      fetchConversationMessages(activeConversation);
      fetchChannelTopics(activeConversation);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeConversationId, activeTopic, zulipBasicAuthHeader]);

  useEffect(() => {
    bootstrapDirectMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [zulipBasicAuthHeader, zulipUserId]);

  useEffect(() => {
    if (!zulipBasicAuthHeader) {
      return;
    }
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }
    const userIds = Object.keys(zulipEmailByUserId);
    if (userIds.length === 0) {
      return;
    }

    let cancelled = false;
    const fetchPresence = async () => {
      try {
        const response = await fetch(`${baseUrl}/realm/presence`, {
          method: "GET",
          headers: {
            Authorization: zulipBasicAuthHeader,
          },
        });
        if (!response.ok || cancelled) {
          return;
        }
        const data: ZulipPresenceResponse = await response.json();
        const presences = data.presences ?? {};
        const presencesByLowerEmail = Object.fromEntries(
          Object.entries(presences).map(([email, value]) => [
            email.toLowerCase(),
            value,
          ])
        );
        const next: Record<number, "active" | "idle" | "offline"> = {};
        for (const [userIdText, email] of Object.entries(zulipEmailByUserId)) {
          const userId = Number(userIdText);
          const byEmail = presencesByLowerEmail[email.toLowerCase()];
          next[userId] = resolveFreshPresenceStatus(byEmail);
        }
        setPresenceByUserId(next);
      } catch (error) {
        console.warn("Failed to fetch Zulip presence:", error);
      }
    };

    void fetchPresence();
    const intervalId = setInterval(() => {
      void fetchPresence();
    }, 10000);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [zulipBasicAuthHeader, zulipEmailByUserId]);

  useEffect(() => {
    setActiveChatConversationId(activeConversationId || null);
  }, [activeConversationId, setActiveChatConversationId]);

  useEffect(() => {
    // Opening a conversation should immediately clear its unread badge.
    if (!activeConversationId) {
      return;
    }
    clearConversationUnread(activeConversationId);
  }, [activeConversationId, clearConversationUnread]);

  useEffect(() => {
    setMessageActionsMenuOpenId(null);
    setDeleteConfirmMessageId(null);
    setDeleteConfirmSenderZulipId(null);
    setChatImagePreview(null);
    setChatImageDownloadBusy(false);
    setChatImagePreviewZoom(1);
    setShowComposerFormattingToolbar(false);
    setComposerReplyStrip(null);
  }, [activeConversationId]);

  useEffect(() => {
    const startEventsPolling = async () => {
      if (!zulipBasicAuthHeader) {
        return;
      }
      const sessionId = ++eventsSessionRef.current;
      const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
      if (!baseUrl) {
        return;
      }

      try {
        const registerPayload = new URLSearchParams();
        registerPayload.set(
          "event_types",
          JSON.stringify([
            "message",
            "reaction",
            "presence",
            "update_message",
            "delete_message",
            "subscription",
            "update_message_flags",
          ])
        );

        const registerResponse = await fetch(`${baseUrl}/register`, {
          method: "POST",
          headers: {
            Authorization: zulipBasicAuthHeader,
            "Content-Type": "application/x-www-form-urlencoded",
          },
          body: registerPayload.toString(),
        });
        if (!registerResponse.ok) {
          return;
        }

        const registerData: ZulipRegisterResponse =
          await registerResponse.json();
        let lastEventId = registerData.last_event_id;

        while (
          !eventsStopRef.current &&
          sessionId === eventsSessionRef.current
        ) {
          const eventsUrl =
            `${baseUrl}/events?queue_id=${encodeURIComponent(registerData.queue_id)}` +
            `&last_event_id=${encodeURIComponent(String(lastEventId))}`;
          const eventsResponse = await fetch(eventsUrl, {
            method: "GET",
            headers: { Authorization: zulipBasicAuthHeader },
          });
          if (!eventsResponse.ok) {
            const retryDelay = eventsResponse.status === 429 ? 2500 : 1200;
            await new Promise(resolve => setTimeout(resolve, retryDelay));
            continue;
          }

          const eventsData: ZulipEventsResponse = await eventsResponse.json();
          if (
            !Array.isArray(eventsData.events) ||
            eventsData.events.length === 0
          ) {
            await new Promise(resolve => setTimeout(resolve, 350));
            continue;
          }

          for (const event of eventsData.events) {
            lastEventId = Math.max(lastEventId, event.id);
            if (event.type === "message" && event.message) {
              const conversationId = resolveConversationIdFromEventMessage(
                event.message
              );
              if (!conversationId) {
                continue;
              }
              const mapped = mapSingleZulipMessage(
                {
                  id: event.message.id,
                  sender_id: event.message.sender_id,
                  sender_full_name: event.message.sender_full_name,
                  content: event.message.content,
                  timestamp: event.message.timestamp,
                  reactions: event.message.reactions,
                  flags: event.message.flags,
                },
                conversationId
              );

              setRemoteMessagesByConversation(prev => {
                const existing = prev[conversationId] ?? [];
                if (existing.some(msg => msg.id === mapped.id)) {
                  return prev;
                }
                return {
                  ...prev,
                  [conversationId]: [...existing, mapped],
                };
              });

              continue;
            }

            if (
              event.type === "reaction" &&
              typeof event.message_id === "number" &&
              typeof event.emoji_name === "string"
            ) {
              const reactionEmoji =
                SYMBOL_BY_EMOJI_NAME[event.emoji_name] ?? "🙂";
              const targetMessageId = `zulip-${event.message_id}`;
              let updatedConversationId: string | null = null;

              setRemoteMessagesByConversation(prev => {
                const next = { ...prev };
                for (const key of Object.keys(next)) {
                  const list = next[key] ?? [];
                  const idx = list.findIndex(msg => msg.id === targetMessageId);
                  if (idx === -1) {
                    continue;
                  }
                  updatedConversationId = key;
                  const target = list[idx];
                  const reactions = [...(target.reactions ?? [])];
                  const ridx = reactions.findIndex(
                    r => r.emoji === reactionEmoji
                  );
                  const isDmConversation = key.startsWith("dm-");
                  if (isDmConversation) {
                    next[key] = list.map((msg, i) =>
                      i === idx
                        ? {
                            ...target,
                            reactions:
                              event.op === "remove"
                                ? []
                                : [
                                    {
                                      emoji: reactionEmoji,
                                      count: 1,
                                      reactedByMe:
                                        event.user_id === zulipUserId,
                                    },
                                  ],
                          }
                        : msg
                    );
                    break;
                  }
                  if (event.op === "remove") {
                    if (ridx >= 0) {
                      reactions[ridx] = {
                        ...reactions[ridx],
                        count: Math.max(0, reactions[ridx].count - 1),
                        reactedByMe:
                          event.user_id === zulipUserId
                            ? false
                            : reactions[ridx].reactedByMe,
                      };
                      if (reactions[ridx].count === 0) {
                        reactions.splice(ridx, 1);
                      }
                    }
                  } else if (ridx >= 0) {
                    reactions[ridx] = {
                      ...reactions[ridx],
                      count: reactions[ridx].count + 1,
                      reactedByMe:
                        reactions[ridx].reactedByMe ??
                        event.user_id === zulipUserId,
                    };
                  } else {
                    reactions.push({
                      emoji: reactionEmoji,
                      count: 1,
                      reactedByMe: event.user_id === zulipUserId,
                    });
                  }
                  next[key] = list.map((msg, i) =>
                    i === idx ? { ...target, reactions } : msg
                  );
                  break;
                }
                return next;
              });

              if (
                updatedConversationId &&
                updatedConversationId !== activeConversationId &&
                event.user_id !== zulipUserId
              ) {
                bumpUnread(updatedConversationId as string);
              }
              continue;
            }

            if (
              event.type === "update_message" &&
              typeof event.message_id === "number" &&
              typeof event.content === "string"
            ) {
              const targetMessageId = `zulip-${event.message_id}`;
              const newText = normalizeZulipContent(event.content);
              const newHtml = formatMessageContent(
                event.content,
                zulipBasicAuthHeader
              );
              setRemoteMessagesByConversation(prev => {
                const next = { ...prev };
                for (const key of Object.keys(next)) {
                  const list = next[key] ?? [];
                  if (!list.some(m => m.id === targetMessageId)) {
                    continue;
                  }
                  next[key] = list.map(m =>
                    m.id === targetMessageId
                      ? {
                          ...m,
                          text: newText,
                          htmlContent: newHtml,
                        }
                      : m
                  );
                }
                return next;
              });
              setMessages(prev =>
                prev.map(m =>
                  m.id === targetMessageId
                    ? {
                        ...m,
                        text: newText,
                        htmlContent: newHtml,
                      }
                    : m
                )
              );
              continue;
            }

            if (event.type === "update_message_flags") {
              const flagOp = event.op ?? event.operation;
              if (
                (flagOp === "add" || flagOp === "remove") &&
                event.flag === "starred"
              ) {
                const ids =
                  Array.isArray(event.messages) && event.messages.length > 0
                    ? event.messages
                    : Array.isArray(event.message_ids) &&
                        event.message_ids.length > 0
                      ? event.message_ids
                      : [];
                if (ids.length === 0) {
                  continue;
                }
                const starred = flagOp === "add";
                setRemoteMessagesByConversation(prev => {
                  const next = { ...prev };
                  for (const key of Object.keys(next)) {
                    const list = next[key] ?? [];
                    let changed = false;
                    const mapped = list.map(m => {
                      const zid = parseZulipNumericMessageId(m.id);
                      if (zid === null || !ids.includes(zid)) {
                        return m;
                      }
                      if (m.starredByMe === starred) {
                        return m;
                      }
                      changed = true;
                      return { ...m, starredByMe: starred };
                    });
                    if (changed) {
                      next[key] = mapped;
                    }
                  }
                  return next;
                });
                setMessages(prev =>
                  prev.map(m => {
                    const zid = parseZulipNumericMessageId(m.id);
                    if (zid === null || !ids.includes(zid)) {
                      return m;
                    }
                    return { ...m, starredByMe: starred };
                  })
                );
              }
              continue;
            }

            if (event.type === "delete_message") {
              const ids =
                Array.isArray(event.message_ids) && event.message_ids.length > 0
                  ? event.message_ids
                  : typeof event.message_id === "number"
                    ? [event.message_id]
                    : [];
              if (ids.length === 0) {
                continue;
              }
              setRemoteMessagesByConversation(prev => {
                const next = { ...prev };
                for (const key of Object.keys(next)) {
                  next[key] = (next[key] ?? []).filter(
                    m => !ids.some(id => m.id === `zulip-${id}`)
                  );
                }
                return next;
              });
              setMessages(prev =>
                prev.filter(m => !ids.some(id => m.id === `zulip-${id}`))
              );
              continue;
            }

            if (event.type === "presence" && event.email && event.presence) {
              const targetUserId =
                userIdByLowerEmail[event.email.toLowerCase()];
              if (!targetUserId) {
                continue;
              }
              const nextStatus = resolveFreshPresenceStatus(event.presence);
              setPresenceByUserId(prev => ({
                ...prev,
                [targetUserId]: nextStatus,
              }));
              continue;
            }

            if (event.type === "subscription") {
              const auth = zulipBasicAuthHeader;
              const bu = import.meta.env.VITE_PTS_API_BASE_URL;
              const refetch = fetchStreamsRef.current;
              if (auth && bu && refetch) {
                void (async () => {
                  await refetch(auth, bu);
                  if (
                    event.op === "add" &&
                    Array.isArray(event.subscriptions) &&
                    event.subscriptions.length > 0
                  ) {
                    const last =
                      event.subscriptions[event.subscriptions.length - 1];
                    if (typeof last.stream_id === "number") {
                      setSidebarChannelSpotlightId(`ch-${last.stream_id}`);
                    }
                  }
                })();
              }
              continue;
            }
          }
        }
      } catch (error) {
        console.warn("Failed to poll Zulip events:", error);
      }
    };

    eventsStopRef.current = false;
    startEventsPolling();

    return () => {
      eventsStopRef.current = true;
      eventsSessionRef.current += 1;
    };
  }, [
    activeConversationId,
    bumpUnread,
    mapSingleZulipMessage,
    resolveConversationIdFromEventMessage,
    userIdByLowerEmail,
    zulipBasicAuthHeader,
    zulipUserId,
  ]);

  const handleSend = () => {
    const content = messageInput.trim();
    if (!content || !activeConversationId) {
      return;
    }

    const now = new Date();
    const time = now.toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: false,
    });

    setMessages(prev => [
      ...prev,
      {
        id: `${Date.now()}`,
        conversationId: activeConversationId,
        author: "You",
        authorCode: "YO",
        tone: "orange",
        text: content,
        timeLabel: time,
        sortKey: Date.now(),
      },
    ]);
    setMessageInput("");
    setComposerReplyStrip(null);
    setShowEmojiPicker(false);
    setShowComposerFormattingToolbar(false);
    closeMentionSuggestions();
  };

  const handleSendToActiveConversation = async () => {
    if (!activeConversation || !zulipBasicAuthHeader) {
      handleSend();
      return;
    }
    const content = messageInput.trim();
    if (!content) {
      return;
    }
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      handleSend();
      return;
    }

    setSendingDmMessage(true);
    try {
      const payload = new URLSearchParams();
      if (activeConversation.type === "dm" && activeConversation.zulipUserId) {
        payload.set("type", "direct");
        payload.set("to", JSON.stringify([activeConversation.zulipUserId]));
      } else {
        payload.set("type", "stream");
        payload.set("to", activeConversation.name);
        payload.set("topic", activeTopic || "general");
      }
      payload.set("content", content);

      const response = await fetch(`${baseUrl}/messages`, {
        method: "POST",
        headers: {
          Authorization: zulipBasicAuthHeader,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });

      if (!response.ok) {
        throw new Error("Failed to send Zulip message");
      }

      setMessageInput("");
      setComposerReplyStrip(null);
      setShowEmojiPicker(false);
      setShowComposerFormattingToolbar(false);
      closeMentionSuggestions();
      await fetchConversationMessages(activeConversation);
      if (activeConversation.type === "dm") {
        await bootstrapDirectMessages();
      }
    } catch (error) {
      console.warn("Failed to send Zulip message:", error);
      handleSend();
    } finally {
      setSendingDmMessage(false);
    }
  };

  const handleCreateTopic = async () => {
    const topic = newTopicName.trim();
    if (
      !topic ||
      !activeConversation ||
      activeConversation.type !== "channel" ||
      !zulipBasicAuthHeader
    ) {
      return;
    }

    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    setCreatingTopic(true);
    try {
      const payload = new URLSearchParams();
      payload.set("type", "stream");
      payload.set("to", activeConversation.name);
      payload.set("topic", topic);
      payload.set("content", `Created topic: ${topic}`);

      const response = await fetch(`${baseUrl}/messages`, {
        method: "POST",
        headers: {
          Authorization: zulipBasicAuthHeader,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });
      if (!response.ok) {
        throw new Error("Failed to create topic");
      }

      setTopicsByChannel(prev => ({
        ...prev,
        [activeConversation.id]: Array.from(
          new Set([...(prev[activeConversation.id] ?? []), topic])
        ),
      }));
      setActiveTopic(topic);
      setNewTopicName("");
      await fetchChannelTopics(activeConversation);
      await fetchConversationMessages(activeConversation);
    } catch (error) {
      console.warn("Failed to create Zulip topic:", error);
    } finally {
      setCreatingTopic(false);
    }
  };

  const handleCreateChannel = async () => {
    const channelName = newChannelName.trim();
    if (!channelName || !zulipBasicAuthHeader) {
      return;
    }

    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    setCreatingChannel(true);
    try {
      const membersToSubscribe = Array.from(
        new Set([
          ...newChannelMemberIds,
          ...(zulipUserId !== null && !newChannelMemberIds.includes(zulipUserId)
            ? [zulipUserId]
            : []),
        ])
      );
      const payload = new URLSearchParams();
      payload.set("subscriptions", JSON.stringify([{ name: channelName }]));
      if (newChannelInviteOnly) {
        payload.set("invite_only", "true");
      }
      if (membersToSubscribe.length > 0) {
        payload.set("principals", JSON.stringify(membersToSubscribe));
      }

      const response = await fetch(`${baseUrl}/users/me/subscriptions`, {
        method: "POST",
        headers: {
          Authorization: zulipBasicAuthHeader,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });
      if (!response.ok) {
        throw new Error("Failed to create channel");
      }

      const nameLc = channelName.toLowerCase();
      let channelsAfterCreate = await fetchStreams(
        zulipBasicAuthHeader,
        baseUrl
      );
      let created = channelsAfterCreate.find(
        channel => channel.name.toLowerCase() === nameLc
      );
      if (!created) {
        await new Promise<void>(resolve => {
          window.setTimeout(resolve, 320);
        });
        channelsAfterCreate = await fetchStreams(zulipBasicAuthHeader, baseUrl);
        created = channelsAfterCreate.find(
          channel => channel.name.toLowerCase() === nameLc
        );
      }
      if (!created) {
        const fromSubs = await fetchChannelFromMySubscriptions(
          channelName,
          zulipBasicAuthHeader,
          baseUrl
        );
        if (fromSubs) {
          created = fromSubs;
          setStreamChannels(prev =>
            prev.some(
              p =>
                (typeof p.streamId === "number" &&
                  typeof fromSubs.streamId === "number" &&
                  p.streamId === fromSubs.streamId) ||
                p.name.toLowerCase() === nameLc
            )
              ? prev
              : [fromSubs, ...prev]
          );
        }
      }

      if (created) {
        const allSubscriberIds =
          membersToSubscribe.length > 0
            ? membersToSubscribe
            : zulipUserId !== null
              ? [zulipUserId]
              : [];
        setActiveConversationId(created.id);
        setActiveTopic("general");
        setChannelMembersByConversationId(prev => ({
          ...prev,
          [created.id]: allSubscriberIds,
        }));
        setSidebarChannelSpotlightId(created.id);

        const inviteesForWelcome = membersToSubscribe.filter(
          uid => zulipUserId === null || uid !== zulipUserId
        );
        if (inviteesForWelcome.length > 0) {
          const mentionLine = inviteesForWelcome
            .map(uid => dmUsers.find(u => u.zulipUserId === uid)?.name)
            .filter((n): n is string => Boolean(n?.trim()))
            .map(n => zulipMentionMarkdownForDisplayName(n))
            .join(" ");
          if (mentionLine) {
            const welcomeOk = await postZulipStreamTopicMessage(
              baseUrl,
              zulipBasicAuthHeader,
              created.name,
              "general",
              `${mentionLine} — you've been added to **${created.name}** (new channel).`
            );
            if (!welcomeOk) {
              console.warn(
                "Channel created but failed to post invite notification message."
              );
            }
          }
        }
        await fetchConversationMessages(created);
      }

      setNewChannelName("");
      setNewChannelInviteOnly(false);
      setNewChannelMemberIds([]);
      setNewChannelMemberSearch("");
      setShowCreateChannel(false);
    } catch (error) {
      console.warn("Failed to create Zulip channel:", error);
    } finally {
      setCreatingChannel(false);
    }
  };

  const fetchChannelMembers = async (channel: Conversation) => {
    if (channel.type !== "channel" || !zulipBasicAuthHeader) {
      return;
    }
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    setLoadingChannelMembers(true);
    try {
      const response = await fetch(
        `${baseUrl}/users/me/subscriptions?include_subscribers=true`,
        {
          method: "GET",
          headers: {
            Authorization: zulipBasicAuthHeader,
          },
        }
      );
      if (!response.ok) {
        throw new Error("Failed to fetch channel subscriptions");
      }
      const data: ZulipSubscriptionsResponse = await response.json();
      const subscriptions = Array.isArray(data.subscriptions)
        ? data.subscriptions
        : [];
      const matched = subscriptions.find(
        item =>
          item.name.toLowerCase() === channel.name.toLowerCase() ||
          (typeof channel.streamId === "number" &&
            item.stream_id === channel.streamId)
      );
      setChannelMembersByConversationId(prev => ({
        ...prev,
        [channel.id]: Array.isArray(matched?.subscribers)
          ? matched?.subscribers
          : [],
      }));
    } catch (error) {
      console.warn("Failed to fetch Zulip channel members:", error);
    } finally {
      setLoadingChannelMembers(false);
    }
  };

  const handleOpenChannelMembersModal = async () => {
    if (!activeConversation || activeConversation.type !== "channel") {
      return;
    }
    setShowChannelMembersModal(true);
    setChannelMemberSearchText("");
    await fetchChannelMembers(activeConversation);
  };

  const handleChannelMemberUpdate = async (
    mode: "add" | "remove",
    user: Conversation
  ) => {
    if (
      !activeConversation ||
      activeConversation.type !== "channel" ||
      !activeConversation.name ||
      !user.zulipUserId ||
      !zulipBasicAuthHeader
    ) {
      return;
    }
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!baseUrl) {
      return;
    }

    setUpdatingChannelMemberUserId(user.zulipUserId);
    try {
      const payload = new URLSearchParams();
      if (mode === "add") {
        payload.set(
          "subscriptions",
          JSON.stringify([{ name: activeConversation.name }])
        );
      } else {
        payload.set("subscriptions", JSON.stringify([activeConversation.name]));
      }
      payload.set("principals", JSON.stringify([user.zulipUserId]));

      const response = await fetch(`${baseUrl}/users/me/subscriptions`, {
        method: mode === "add" ? "POST" : "DELETE",
        headers: {
          Authorization: zulipBasicAuthHeader,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });
      if (!response.ok) {
        throw new Error(
          mode === "add"
            ? "Failed to add member to channel"
            : "Failed to remove member from channel"
        );
      }

      if (mode === "add") {
        const mention = zulipMentionMarkdownForDisplayName(user.name);
        const topic = activeTopic?.trim() || "general";
        const welcomeOk = await postZulipStreamTopicMessage(
          baseUrl,
          zulipBasicAuthHeader,
          activeConversation.name,
          topic,
          `${mention} — you've been added to **${activeConversation.name}**.`
        );
        if (!welcomeOk) {
          console.warn(
            "Member added but failed to post channel notification message."
          );
        }
        await fetchConversationMessages(activeConversation);
      }

      await fetchChannelMembers(activeConversation);
    } catch (error) {
      console.warn("Failed to update Zulip channel member:", error);
    } finally {
      setUpdatingChannelMemberUserId(null);
    }
  };

  const appendToMessage = (value: string) => {
    setMessageInput(prev => `${prev}${value}`);
  };

  const closeMentionSuggestions = useCallback(() => {
    setMentionStartIndex(null);
    setMentionQuery("");
    setActiveMentionIndex(0);
  }, []);

  const syncMentionQueryFromInput = useCallback(
    (value: string, caretPosition: number) => {
      const uptoCaret = value.slice(0, caretPosition);
      const match = uptoCaret.match(/(^|\s)@([^\s@]*)$/);
      if (!match) {
        closeMentionSuggestions();
        return;
      }
      const query = match[2] ?? "";
      const startIndex = caretPosition - query.length - 1;
      setMentionStartIndex(startIndex);
      setMentionQuery(query);
      setActiveMentionIndex(0);
    },
    [closeMentionSuggestions]
  );

  const handleMessageInputChange = (value: string, caretPosition: number) => {
    setMessageInput(value);
    syncMentionQueryFromInput(value, caretPosition);
  };

  const applyComposerMarkdownWrap = useCallback(
    (before: string, after: string) => {
      const el = messageInputRef.current;
      const start = el?.selectionStart ?? messageInput.length;
      const end = el?.selectionEnd ?? start;
      const { next, caret } = wrapSelectionWithMarkdown(
        messageInput,
        start,
        end,
        before,
        after
      );
      setMessageInput(next);
      syncMentionQueryFromInput(next, caret);
      window.requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(caret, caret);
      });
    },
    [messageInput, syncMentionQueryFromInput]
  );

  const applyComposerLineMap = useCallback(
    (mapLine: (line: string, index: number) => string) => {
      const el = messageInputRef.current;
      const start = el?.selectionStart ?? messageInput.length;
      const end = el?.selectionEnd ?? start;
      const { next, caret } = mapLinesInSelection(
        messageInput,
        start,
        end,
        mapLine
      );
      setMessageInput(next);
      syncMentionQueryFromInput(next, caret);
      window.requestAnimationFrame(() => {
        el?.focus();
        el?.setSelectionRange(caret, caret);
      });
    },
    [messageInput, syncMentionQueryFromInput]
  );

  const insertComposerMarkdownLink = useCallback(() => {
    const el = messageInputRef.current;
    const start = el?.selectionStart ?? messageInput.length;
    const end = el?.selectionEnd ?? start;
    const selected = messageInput.slice(
      Math.min(start, end),
      Math.max(start, end)
    );
    const url = window.prompt("Link URL", "https://");
    if (url === null || url.trim() === "") {
      return;
    }
    const trimmedSelected = selected.trim();
    const promptText = window.prompt(
      "Link text (shown in message)",
      url.trim()
    );
    const trimmedPrompt = promptText?.trim() ?? "";
    const label =
      trimmedSelected !== ""
        ? trimmedSelected
        : trimmedPrompt !== ""
          ? trimmedPrompt
          : url.trim();
    const md = `[${label}](${url.trim()})`;
    const a = Math.min(start, end);
    const b = Math.max(start, end);
    const next = `${messageInput.slice(0, a)}${md}${messageInput.slice(b)}`;
    const caret = a + md.length;
    setMessageInput(next);
    syncMentionQueryFromInput(next, caret);
    window.requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }, [messageInput, syncMentionQueryFromInput]);

  const insertComposerFencedCode = useCallback(() => {
    const el = messageInputRef.current;
    const start = el?.selectionStart ?? messageInput.length;
    const fragment = "```\n\n```";
    const next =
      messageInput.slice(0, start) + fragment + messageInput.slice(start);
    const caret = start + 4;
    setMessageInput(next);
    syncMentionQueryFromInput(next, caret);
    window.requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }, [messageInput, syncMentionQueryFromInput]);

  const insertComposerHorizontalRule = useCallback(() => {
    const el = messageInputRef.current;
    const start = el?.selectionStart ?? messageInput.length;
    const prefix =
      messageInput.slice(Math.max(0, start - 1), start) === "\n" ? "" : "\n";
    const fragment = `${prefix}---\n`;
    const next =
      messageInput.slice(0, start) + fragment + messageInput.slice(start);
    const caret = start + fragment.length;
    setMessageInput(next);
    syncMentionQueryFromInput(next, caret);
    window.requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(caret, caret);
    });
  }, [messageInput, syncMentionQueryFromInput]);

  const insertComposerHeading = useCallback(() => {
    applyComposerLineMap((line, _i) =>
      /^#{1,6}\s/.test(line) ? line : `## ${line.replace(/^#{1,6}\s*/, "")}`
    );
  }, [applyComposerLineMap]);

  const composerToolbarButtonClass =
    "h-7 w-7 shrink-0 text-slate-600 hover:bg-slate-200 hover:text-slate-900";

  const insertMention = (option: MentionOption) => {
    if (mentionStartIndex === null) {
      return;
    }
    const textarea = messageInputRef.current;
    const caretPosition = textarea?.selectionStart ?? messageInput.length;
    const beforeMention = messageInput.slice(0, mentionStartIndex);
    const afterMention = messageInput.slice(caretPosition);
    const mentionToken = `${option.mentionText} `;
    const nextValue = `${beforeMention}${mentionToken}${afterMention}`;
    const nextCursor = beforeMention.length + mentionToken.length;

    setMessageInput(nextValue);
    closeMentionSuggestions();
    requestAnimationFrame(() => {
      textarea?.focus();
      textarea?.setSelectionRange(nextCursor, nextCursor);
    });
  };

  const handleEmojiSelect = (emoji: string) => {
    appendToMessage(`${emoji} `);
    setShowEmojiPicker(false);
  };

  const handleAttachmentUpload = async (
    event: ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!zulipBasicAuthHeader || !baseUrl) {
      event.target.value = "";
      return;
    }

    setUploadingAttachment(true);
    try {
      const payload = new FormData();
      payload.append("filename", file);
      const response = await fetch(`${baseUrl}/user_uploads`, {
        method: "POST",
        headers: {
          Authorization: zulipBasicAuthHeader,
        },
        body: payload,
      });
      if (!response.ok) {
        throw new Error("Failed to upload attachment");
      }
      const data: ZulipUploadResponse = await response.json();
      const uploadedUrl = data.uri ?? data.url;
      if (!uploadedUrl) {
        throw new Error("Upload API did not return file URL");
      }
      const safeName = file.name.replace(/\]/g, "");
      appendToMessage(`[${safeName}](${uploadedUrl}) `);
    } catch (error) {
      console.warn("Failed to upload attachment:", error);
    } finally {
      event.target.value = "";
      setUploadingAttachment(false);
    }
  };

  const parseZulipNumericMessageId = (messageId: string): number | null => {
    if (!messageId.startsWith("zulip-")) {
      return null;
    }
    const n = Number(messageId.replace("zulip-", ""));
    return Number.isNaN(n) ? null : n;
  };

  const writeTextToClipboard = async (text: string) => {
    const t = text.trim();
    if (!t) {
      return false;
    }
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(t);
        return true;
      }
    } catch {
      /* fall through */
    }
    try {
      const ta = document.createElement("textarea");
      ta.value = t;
      ta.setAttribute("readonly", "");
      ta.style.position = "fixed";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand("copy");
      document.body.removeChild(ta);
      return ok;
    } catch {
      return false;
    }
  };

  const buildZulipQuoteReplyPrefix = (
    author: string,
    quotedBody: string,
    quotedTimestampLine?: string
  ) => {
    const safeAuthor = author.replace(/\*/g, "").trim() || "User";
    const fenceSafe = quotedBody.replace(/```/g, "`\u200b``");
    const meta = quotedTimestampLine?.trim()
      ? `${quotedTimestampLine.trim()}\n`
      : "";
    return `@**${safeAuthor}**:\n\`\`\`quote\n${meta}${fenceSafe}\n\`\`\`\n\n`;
  };

  const handleCancelComposerReply = () => {
    if (!composerReplyStrip) {
      return;
    }
    const { prefixLen } = composerReplyStrip;
    setMessageInput(prev => {
      if (prev.length >= prefixLen) {
        return prev.slice(prefixLen).replace(/^\s+/, "");
      }
      return prev;
    });
    setComposerReplyStrip(null);
  };

  const handleCopyMessageContent = async (message: ChatMessage) => {
    const text = plainTextFromMessageForClipboard(message);
    const ok = await writeTextToClipboard(text);
    setMessageActionsMenuOpenId(null);
    if (ok && text) {
      antdMessage.success("Copied");
    } else if (!text) {
      antdMessage.warning("Nothing to copy");
    } else {
      antdMessage.error("Copy failed");
    }
  };

  const handleReplyToMessage = (message: ChatMessage) => {
    const quotedAt = formatQuotedSourceTimestamp(
      message.sortKey,
      message.timeLabel
    );
    const quoteSource =
      plainTextFromMessageForClipboard(message) || "(empty message)";
    const prefix = buildZulipQuoteReplyPrefix(
      message.author,
      quoteSource,
      quotedAt || undefined
    );
    setMessageInput(prev => (prev.trim() ? `${prefix}${prev}` : prefix));
    setComposerReplyStrip({
      prefixLen: prefix.length,
      replyToLabel: message.author,
    });
    setMessageActionsMenuOpenId(null);
    closeMentionSuggestions();
    requestAnimationFrame(() => {
      const el = messageInputRef.current;
      el?.focus();
      try {
        if (el) {
          const end = el.value.length;
          el.setSelectionRange(end, end);
        }
      } catch {
        /* ignore */
      }
    });
  };

  const updateMessageTextEverywhere = (messageId: string, newText: string) => {
    setRemoteMessagesByConversation(prev => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        const list = next[key] ?? [];
        if (!list.some(m => m.id === messageId)) {
          continue;
        }
        next[key] = list.map(m =>
          m.id === messageId
            ? { ...m, text: newText, htmlContent: undefined }
            : m
        );
      }
      return next;
    });
    setMessages(prev =>
      prev.map(m =>
        m.id === messageId ? { ...m, text: newText, htmlContent: undefined } : m
      )
    );
  };

  const updateMessageStarredEverywhere = (
    messageId: string,
    starredByMe: boolean
  ) => {
    setRemoteMessagesByConversation(prev => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        const list = next[key] ?? [];
        if (!list.some(m => m.id === messageId)) {
          continue;
        }
        next[key] = list.map(m =>
          m.id === messageId ? { ...m, starredByMe } : m
        );
      }
      return next;
    });
    setMessages(prev =>
      prev.map(m => (m.id === messageId ? { ...m, starredByMe } : m))
    );
  };

  const handleToggleZulipMessageStar = async (message: ChatMessage) => {
    if (!message.id.startsWith("zulip-")) {
      return;
    }
    const zulipId = parseZulipNumericMessageId(message.id);
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!zulipBasicAuthHeader || !baseUrl || zulipId === null) {
      setMessageActionsMenuOpenId(null);
      antdMessage.error("Could not update star");
      return;
    }
    const wasStarred = message.starredByMe === true;
    const op = wasStarred ? "remove" : "add";
    setMessageActionsMenuOpenId(null);
    setMessageMutatingId(message.id);
    try {
      const payload = new URLSearchParams();
      payload.set("messages", JSON.stringify([zulipId]));
      payload.set("op", op);
      payload.set("flag", "starred");
      const response = await fetch(`${baseUrl}/messages/flags`, {
        method: "POST",
        headers: {
          Authorization: zulipBasicAuthHeader,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });
      if (!response.ok) {
        const apiMsg = await readZulipApiErrorMessage(response);
        antdMessage.error(apiMsg);
        return;
      }
      updateMessageStarredEverywhere(message.id, !wasStarred);
      antdMessage.success(wasStarred ? "Unstarred" : "Starred");
    } catch (error) {
      console.warn("Failed to toggle message star:", error);
      antdMessage.error("Could not update star");
    } finally {
      setMessageMutatingId(null);
    }
  };

  const removeMessageEverywhere = (messageId: string) => {
    setRemoteMessagesByConversation(prev => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        next[key] = (next[key] ?? []).filter(m => m.id !== messageId);
      }
      return next;
    });
    setMessages(prev => prev.filter(m => m.id !== messageId));
  };

  const handleStartEditMessage = (message: ChatMessage) => {
    if (!ptsChatMessageEditEnabled) {
      return;
    }
    if (
      zulipUserId === null ||
      message.senderZulipUserId !== zulipUserId ||
      !message.id.startsWith("zulip-")
    ) {
      return;
    }
    setEditingMessageId(message.id);
    setEditingMessageSenderZulipId(message.senderZulipUserId);
    setEditingMessageDraft(message.text);
    setMessageEditError(null);
    setReactionPickerForMessage(null);
  };

  const handleCancelEditMessage = () => {
    setEditingMessageId(null);
    setEditingMessageSenderZulipId(null);
    setEditingMessageDraft("");
    setMessageEditError(null);
  };

  const handleSaveEditMessage = async () => {
    const messageId = editingMessageId;
    const content = editingMessageDraft.trim();
    if (!messageId || !content) {
      return;
    }
    if (zulipUserId === null || editingMessageSenderZulipId !== zulipUserId) {
      handleCancelEditMessage();
      return;
    }
    if (!ptsChatMessageEditEnabled) {
      handleCancelEditMessage();
      return;
    }
    const zulipId = parseZulipNumericMessageId(messageId);
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!zulipBasicAuthHeader || !baseUrl || zulipId === null) {
      handleCancelEditMessage();
      return;
    }

    setMessageMutatingId(messageId);
    setMessageEditError(null);
    try {
      const payload = new URLSearchParams();
      payload.set("content", content);
      const response = await fetch(`${baseUrl}/messages/${zulipId}`, {
        method: "PATCH",
        headers: {
          Authorization: zulipBasicAuthHeader,
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: payload.toString(),
      });
      if (!response.ok) {
        const apiMsg = await readZulipApiErrorMessage(response);
        setMessageEditError(apiMsg);
        return;
      }
      updateMessageTextEverywhere(messageId, content);
      setMessageEditError(null);
      handleCancelEditMessage();
    } catch (error) {
      console.warn("Failed to edit Zulip message:", error);
      setMessageEditError(
        error instanceof Error && error.message
          ? error.message
          : "Could not save your edit. Check your connection and try again."
      );
    } finally {
      setMessageMutatingId(null);
    }
  };

  useEffect(() => {
    if (!editingMessageId) {
      return;
    }
    const el = messageEditTextareaRef.current;
    if (!el) {
      return;
    }
    el.focus();
    const len = el.value.length;
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(len, len);
      } catch {
        /* ignore */
      }
    });
  }, [editingMessageId]);

  const performMessageDeletion = async (messageId: string) => {
    if (!ptsChatMessageDeleteEnabled) {
      setDeleteConfirmMessageId(null);
      setDeleteConfirmSenderZulipId(null);
      return;
    }
    if (
      zulipUserId === null ||
      deleteConfirmSenderZulipId !== zulipUserId ||
      messageId !== deleteConfirmMessageId
    ) {
      setDeleteConfirmMessageId(null);
      setDeleteConfirmSenderZulipId(null);
      return;
    }
    const zulipId = parseZulipNumericMessageId(messageId);
    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    if (!zulipBasicAuthHeader || !baseUrl || zulipId === null) {
      return;
    }

    setMessageMutatingId(messageId);
    try {
      const response = await fetch(`${baseUrl}/messages/${zulipId}`, {
        method: "DELETE",
        headers: {
          Authorization: zulipBasicAuthHeader,
        },
      });
      if (!response.ok) {
        throw new Error("Failed to delete message");
      }
      if (editingMessageId === messageId) {
        handleCancelEditMessage();
      }
      setReactionPickerForMessage(prev => (prev === messageId ? null : prev));
      removeMessageEverywhere(messageId);
      setDeleteConfirmMessageId(null);
      setDeleteConfirmSenderZulipId(null);
    } catch (error) {
      console.warn("Failed to delete Zulip message:", error);
    } finally {
      setMessageMutatingId(null);
    }
  };

  const closeChatImagePreview = useCallback(() => {
    setChatImagePreview(null);
    setChatImageDownloadBusy(false);
    setChatImagePreviewZoom(1);
  }, []);

  const handleZulipMessageImageClick = useCallback(
    (event: MouseEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }
      const el = event.target as HTMLElement | null;
      if (!el || el.tagName !== "IMG") {
        return;
      }
      const img = el as HTMLImageElement;
      const src = img.currentSrc || img.getAttribute("src");
      if (!src) {
        return;
      }
      const root = event.currentTarget;
      const senderName = root.dataset.previewSender?.trim() ?? "Unknown";
      const timeLabel = root.dataset.previewTime?.trim() ?? "";
      const messageId = root.dataset.previewMessageId?.trim() ?? "";
      const sortKeyRaw = root.dataset.previewSortKey?.trim();
      const sortKey =
        sortKeyRaw && !Number.isNaN(Number(sortKeyRaw))
          ? Number(sortKeyRaw)
          : Date.now();

      event.preventDefault();
      event.stopPropagation();
      setChatImagePreviewZoom(1);
      setChatImagePreview({
        src,
        alt: (img.alt || "Chat image").trim() || "Chat image",
        senderName,
        timeLabel,
        messageId,
        sortKey,
      });
    },
    []
  );

  const showMessageFromImagePreview = useCallback(() => {
    const id = chatImagePreview?.messageId;
    if (!id) {
      return;
    }
    closeChatImagePreview();
    window.requestAnimationFrame(() => {
      document
        .getElementById(`chat-msg-${id}`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    });
  }, [chatImagePreview, closeChatImagePreview]);

  const downloadChatPreviewImage = useCallback(async () => {
    if (!chatImagePreview) {
      return;
    }
    const { src, alt } = chatImagePreview;
    setChatImageDownloadBusy(true);
    const suggestedName = (() => {
      try {
        const u = new URL(src, window.location.origin);
        const last = u.pathname.split("/").filter(Boolean).pop();
        if (last && /\.(png|jpe?g|gif|webp|svg)$/i.test(last)) {
          return last;
        }
      } catch {
        /* ignore */
      }
      const base = alt.replace(/[^\w.-]+/g, "_").slice(0, 72);
      return `${base || "chat-image"}.png`;
    })();

    try {
      const response = await fetch(src, { mode: "cors", credentials: "omit" });
      if (!response.ok) {
        throw new Error("Download failed");
      }
      const blob = await response.blob();
      const mime = blob.type || "image/png";
      const extFromMime = mime.split("/")[1]?.replace(/\+xml$/, "") || "png";
      const hasExt = /\.(png|jpe?g|gif|webp|svg)$/i.test(suggestedName);
      const filename = hasExt
        ? suggestedName
        : `${suggestedName.replace(/\.+$/g, "")}.${extFromMime}`;
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = filename;
      anchor.rel = "noopener";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch {
      window.open(src, "_blank", "noopener,noreferrer");
    } finally {
      setChatImageDownloadBusy(false);
    }
  }, [chatImagePreview]);

  useEffect(() => {
    if (!chatImagePreview) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeChatImagePreview();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [chatImagePreview, closeChatImagePreview]);

  const handleReactionClick = async (
    messageId: string,
    emoji: string,
    emojiName?: string
  ) => {
    const emojiKey = emojiName ?? EMOJI_NAME_BY_SYMBOL[emoji] ?? "smile";
    const messageFromRemote = Object.values(remoteMessagesByConversation)
      .flat()
      .find(message => message.id === messageId);
    const messageFromLocal = messages.find(message => message.id === messageId);
    const existingReaction = (
      messageFromRemote?.reactions ??
      messageFromLocal?.reactions ??
      []
    ).find(item => item.emoji === emoji);
    const removeExisting = Boolean(existingReaction?.reactedByMe);

    const baseUrl = import.meta.env.VITE_PTS_API_BASE_URL;
    const zulipMessageId = messageId.startsWith("zulip-")
      ? Number(messageId.replace("zulip-", ""))
      : null;

    if (
      zulipMessageId &&
      !Number.isNaN(zulipMessageId) &&
      zulipBasicAuthHeader &&
      baseUrl
    ) {
      try {
        const payload = new URLSearchParams();
        payload.set("emoji_name", emojiKey);
        const response = removeExisting
          ? await fetch(`${baseUrl}/messages/${zulipMessageId}/reactions`, {
              method: "DELETE",
              headers: {
                Authorization: zulipBasicAuthHeader,
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: payload.toString(),
            })
          : await fetch(`${baseUrl}/messages/${zulipMessageId}/reactions`, {
              method: "POST",
              headers: {
                Authorization: zulipBasicAuthHeader,
                "Content-Type": "application/x-www-form-urlencoded",
              },
              body: payload.toString(),
            });
        if (!response.ok) {
          throw new Error(
            removeExisting
              ? "Failed to remove reaction"
              : "Failed to add reaction"
          );
        }
      } catch (error) {
        console.warn("Failed to update Zulip reaction:", error);
      }
    }

    const patchList = (list: ChatMessage[], conversationId: string) =>
      list.map(message => {
        if (message.id !== messageId) {
          return message;
        }
        if (conversationId.startsWith("dm-")) {
          return {
            ...message,
            reactions: removeExisting
              ? []
              : [{ emoji, count: 1, reactedByMe: true }],
          };
        }
        const existing = message.reactions ?? [];
        const index = existing.findIndex(item => item.emoji === emoji);
        if (removeExisting) {
          if (index === -1) {
            return message;
          }
          const next = [...existing];
          const current = next[index];
          if (current.count <= 1) {
            next.splice(index, 1);
          } else {
            next[index] = {
              ...current,
              count: current.count - 1,
              reactedByMe: false,
            };
          }
          return { ...message, reactions: next };
        }
        if (index === -1) {
          return {
            ...message,
            reactions: [...existing, { emoji, count: 1, reactedByMe: true }],
          };
        }
        const next = [...existing];
        next[index] = {
          ...next[index],
          count: next[index].count + 1,
          reactedByMe: true,
        };
        return { ...message, reactions: next };
      });

    setRemoteMessagesByConversation(prev => {
      const updated = { ...prev };
      let changed = false;
      for (const key of Object.keys(updated)) {
        const list = updated[key] ?? [];
        if (list.some(message => message.id === messageId)) {
          updated[key] = patchList(list, key);
          changed = true;
        }
      }
      return changed ? updated : prev;
    });
    setMessages(prev => patchList(prev, activeConversationId));
  };

  const handleSelectMessageReaction = async (
    messageId: string,
    emoji: string,
    emojiName?: string
  ) => {
    await handleReactionClick(messageId, emoji, emojiName);
    setReactionPickerForMessage(null);
  };

  return (
    <div className="mx-auto h-[calc(100vh-7rem)] max-h-fit min-h-[34rem] w-full max-w-[96rem] overflow-hidden rounded-2xl border bg-white font-['Nunito'] text-slate-800 shadow-sm">
      <div className="flex h-full">
        <aside className="flex w-[290px] flex-col border-r bg-white">
          <div className="border-b px-4 py-3">
            <h1 className="text-base font-semibold text-[#0b2f57]">
              Team Chat
            </h1>
          </div>

          <div className="p-3">
            <div className="relative">
              <Search className="text-muted-foreground absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2" />
              <Input
                className="h-9 pl-9"
                placeholder="Search channels & people..."
                value={conversationSearchText}
                onChange={event =>
                  setConversationSearchText(event.target.value)
                }
              />
            </div>
          </div>

          <div className="scrollbar-thin-grey min-h-0 flex-1 overflow-y-auto pb-3">
            <div className="px-3">
              <div className="mb-2 flex items-center justify-between gap-1 px-1 text-[11px] font-semibold tracking-wide text-slate-500">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-1 rounded px-0.5 py-0.5 text-left hover:bg-slate-50"
                  onClick={() => setSidebarChannelsOpen(v => !v)}
                  aria-expanded={sidebarChannelsOpen}
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-200 ${
                      sidebarChannelsOpen ? "" : "-rotate-90"
                    }`}
                    aria-hidden
                  />
                  <span>CHANNELS</span>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0"
                  onClick={() => {
                    setNewChannelName("");
                    setNewChannelInviteOnly(false);
                    setNewChannelMemberSearch("");
                    setNewChannelMemberIds(
                      zulipUserId !== null ? [zulipUserId] : []
                    );
                    setShowCreateChannel(true);
                  }}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {sidebarChannelsOpen && (
                <div className="space-y-0.5">
                  {filteredChannels.map(item => {
                    const isActive = item.id === activeConversationId;
                    const isSpotlightChannel =
                      item.id === sidebarChannelSpotlightId;
                    const hasUnreadDot =
                      (unreadByConversationId[item.id] ?? 0) +
                        (item.unread ?? 0) >
                      0;
                    const nameEmphasis = hasUnreadDot || isSpotlightChannel;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          if (item.id === sidebarChannelSpotlightId) {
                            setSidebarChannelSpotlightId(null);
                          }
                          setActiveConversationId(item.id);
                          clearConversationUnread(item.id);
                        }}
                        className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left text-[13px] ${
                          isActive
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <Hash className="h-3.5 w-3.5 text-slate-500" />
                          <span
                            className={`truncate text-sm ${
                              nameEmphasis ? "font-semibold text-slate-900" : ""
                            }`}
                          >
                            {item.name}
                          </span>
                        </div>
                        {(hasUnreadDot || isSpotlightChannel) && (
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-500"
                            title={
                              hasUnreadDot
                                ? "New messages"
                                : "New channel — open to dismiss"
                            }
                          />
                        )}
                      </button>
                    );
                  })}
                  {filteredChannels.length === 0 && (
                    <div className="rounded-md border border-dashed px-2 py-2 text-xs text-slate-500">
                      No channels found.
                    </div>
                  )}
                </div>
              )}
            </div>

            <div className="mt-4 px-3">
              <div className="mb-2 flex items-center justify-between gap-1 px-1 text-[11px] font-semibold tracking-wide text-slate-500">
                <button
                  type="button"
                  className="flex min-w-0 flex-1 items-center gap-1 rounded px-0.5 py-0.5 text-left hover:bg-slate-50"
                  onClick={() => setSidebarDirectMessagesOpen(v => !v)}
                  aria-expanded={sidebarDirectMessagesOpen}
                >
                  <ChevronDown
                    className={`h-3.5 w-3.5 shrink-0 text-slate-500 transition-transform duration-200 ${
                      sidebarDirectMessagesOpen ? "" : "-rotate-90"
                    }`}
                    aria-hidden
                  />
                  <span>DIRECT MESSAGES</span>
                </button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-5 w-5 shrink-0"
                  onClick={() => setShowMembersModal(true)}
                >
                  <Plus className="h-3.5 w-3.5" />
                </Button>
              </div>
              {sidebarDirectMessagesOpen && (
                <div className="space-y-0.5">
                  {filteredDirectMessages.map(item => {
                    const isActive = item.id === activeConversationId;
                    const presenceStatus = getPresenceStatus(item.zulipUserId);
                    const initials = item.name
                      .split(" ")
                      .map(part => part[0])
                      .join("")
                      .slice(0, 2)
                      .toUpperCase();

                    return (
                      <button
                        key={item.id}
                        type="button"
                        onClick={() => {
                          setActiveConversationId(item.id);
                          clearConversationUnread(item.id);
                        }}
                        className={`flex w-full items-center justify-between rounded-md px-2 py-1.5 text-left ${
                          isActive
                            ? "bg-slate-100 text-slate-900"
                            : "text-slate-700 hover:bg-slate-50"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <div className="relative h-8 w-8 shrink-0">
                            <Avatar className="h-8 w-8">
                              <AvatarFallback className="bg-violet-100 text-[10px] font-semibold text-violet-700">
                                {initials}
                              </AvatarFallback>
                            </Avatar>
                            <span
                              className={`absolute -bottom-0.5 -right-0.5 z-10 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                                presenceStatus === "active"
                                  ? "bg-emerald-500"
                                  : presenceStatus === "idle"
                                    ? "bg-amber-400"
                                    : "bg-slate-300"
                              }`}
                              title={`Status: ${presenceStatus}`}
                            />
                          </div>
                          <div className="min-w-0">
                            <span
                              className={`block truncate text-sm ${
                                (unreadByConversationId[item.id] ?? 0) > 0
                                  ? "font-bold text-slate-900"
                                  : ""
                              }`}
                            >
                              {item.name}
                            </span>
                          </div>
                        </div>
                        {(unreadByConversationId[item.id] ?? 0) +
                          (item.unread ?? 0) >
                          0 && (
                          <span
                            className="h-2.5 w-2.5 rounded-full bg-emerald-500"
                            title="New message"
                          />
                        )}
                      </button>
                    );
                  })}
                  {filteredDirectMessages.length === 0 && (
                    <div className="rounded-md border border-dashed px-2 py-2 text-xs text-slate-500">
                      No users found.
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {showMembersModal && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30 p-3 backdrop-blur-sm">
              <div className="w-full max-w-sm rounded-lg bg-white p-3 shadow-lg">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">
                    All Members
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowMembersModal(false)}
                  >
                    Close
                  </Button>
                </div>
                <Input
                  value={memberSearchText}
                  onChange={event => setMemberSearchText(event.target.value)}
                  placeholder="Search members..."
                  className="mb-2 h-8"
                />
                <div className="scrollbar-thin-grey max-h-72 space-y-1 overflow-y-auto">
                  {membersModalUsers.map(user => (
                    <button
                      key={`member-modal-${user.id}`}
                      type="button"
                      onClick={() => {
                        setActiveConversationId(user.id);
                        clearConversationUnread(user.id);
                        setShowMembersModal(false);
                      }}
                      className="flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-slate-50"
                    >
                      <div className="relative h-7 w-7 shrink-0">
                        <Avatar className="h-7 w-7">
                          <AvatarFallback className="bg-violet-100 text-[10px] font-semibold text-violet-700">
                            {user.name
                              .split(" ")
                              .map(part => part[0])
                              .join("")
                              .slice(0, 2)
                              .toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span
                          className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                            getPresenceStatus(user.zulipUserId) === "active"
                              ? "bg-emerald-500"
                              : getPresenceStatus(user.zulipUserId) === "idle"
                                ? "bg-amber-400"
                                : "bg-slate-300"
                          }`}
                          title={`Status: ${getPresenceStatus(user.zulipUserId)}`}
                        />
                      </div>
                      <div className="min-w-0">
                        <span className="block truncate text-sm text-slate-700">
                          {user.name}
                        </span>
                      </div>
                    </button>
                  ))}
                  {membersModalUsers.length === 0 && (
                    <p className="px-2 py-4 text-center text-xs text-slate-500">
                      No members found.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          {showCreateChannel && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30 p-3 backdrop-blur-sm">
              <div className="w-full max-w-md rounded-lg bg-white p-3 shadow-lg">
                <div className="mb-2 flex items-center justify-between">
                  <h3 className="text-sm font-semibold text-slate-800">
                    Create New Channel
                  </h3>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => {
                      setShowCreateChannel(false);
                      setNewChannelName("");
                      setNewChannelInviteOnly(false);
                      setNewChannelMemberIds([]);
                      setNewChannelMemberSearch("");
                    }}
                  >
                    Close
                  </Button>
                </div>

                <div className="mb-2">
                  <Input
                    value={newChannelName}
                    onChange={event => setNewChannelName(event.target.value)}
                    placeholder="Channel name"
                    className="h-8 text-xs"
                  />
                </div>
                <label className="mb-2 flex cursor-pointer items-center justify-between rounded-md border border-slate-200 px-2 py-1.5">
                  <span className="text-xs font-medium text-slate-700">
                    Private channel
                  </span>
                  <input
                    type="checkbox"
                    checked={newChannelInviteOnly}
                    onChange={event =>
                      setNewChannelInviteOnly(event.target.checked)
                    }
                    className="h-4 w-4"
                  />
                </label>

                <div>
                  <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                    Add members ({newChannelMemberIds.length} selected)
                  </p>
                  <p className="mb-1.5 text-[10px] leading-snug text-slate-500">
                    You are subscribed automatically. Invite others with the
                    checkboxes; they get a Zulip mention when the channel is
                    created.
                  </p>
                  <Input
                    value={newChannelMemberSearch}
                    onChange={event =>
                      setNewChannelMemberSearch(event.target.value)
                    }
                    placeholder="Search members..."
                    className="mb-2 h-8 text-xs"
                  />
                  <div className="scrollbar-thin-grey max-h-56 space-y-1 overflow-y-auto rounded-md border border-slate-200 p-1.5">
                    {createChannelMemberCandidates.map(user => {
                      const userId = user.zulipUserId;
                      if (!userId) {
                        return null;
                      }
                      const isSelf =
                        zulipUserId !== null && userId === zulipUserId;
                      const checked =
                        isSelf || newChannelMemberIds.includes(userId);
                      return (
                        <label
                          key={`create-channel-member-${user.id}`}
                          className={`flex items-center justify-between rounded px-1.5 py-1 hover:bg-slate-50 ${
                            isSelf
                              ? "cursor-default bg-slate-50/90"
                              : "cursor-pointer"
                          }`}
                        >
                          <div className="min-w-0">
                            <span className="block truncate text-xs text-slate-700">
                              {user.name}
                              {isSelf ? (
                                <span className="font-normal text-slate-400">
                                  {" "}
                                  (you)
                                </span>
                              ) : null}
                            </span>
                          </div>
                          <input
                            type="checkbox"
                            checked={checked}
                            disabled={isSelf}
                            onChange={event => {
                              if (isSelf) {
                                return;
                              }
                              setNewChannelMemberIds(prev =>
                                event.target.checked
                                  ? Array.from(new Set([...prev, userId]))
                                  : prev.filter(id => id !== userId)
                              );
                            }}
                            className="h-3.5 w-3.5 disabled:cursor-not-allowed disabled:opacity-60"
                          />
                        </label>
                      );
                    })}
                  </div>
                </div>

                <div className="mt-3 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    className="h-8 px-3 text-xs"
                    onClick={() => {
                      setShowCreateChannel(false);
                      setNewChannelName("");
                      setNewChannelInviteOnly(false);
                      setNewChannelMemberIds([]);
                      setNewChannelMemberSearch("");
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    className="h-8 px-3 text-xs"
                    onClick={handleCreateChannel}
                    disabled={creatingChannel || !newChannelName.trim()}
                  >
                    {creatingChannel ? "Creating..." : "Create"}
                  </Button>
                </div>
              </div>
            </div>
          )}
        </aside>

        <section className="relative flex min-w-0 flex-1 flex-col bg-white">
          <header className="flex items-center justify-between border-b px-5 py-2.5">
            <div className="min-w-0">
              {activeConversation?.type === "dm" ? (
                <div className="flex items-start gap-2">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-violet-100 text-[11px] font-semibold text-violet-700">
                      {initialsFromName(activeConversation?.name ?? "User")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="min-w-0">
                    <h2 className="truncate text-sm font-semibold text-slate-900">
                      {activeConversation?.name ?? "No conversation selected"}
                    </h2>
                    <p className="mt-0.5 text-xs text-slate-500">
                      Direct message
                    </p>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <Hash className="h-4 w-4 text-slate-600" />
                    <h2 className="truncate text-sm font-semibold text-slate-900">
                      {activeConversation?.name ?? "No conversation selected"}
                    </h2>
                    {activeConversation?.isPrivate && (
                      <Lock className="h-3.5 w-3.5 text-slate-500" />
                    )}
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {!activeConversation
                      ? "Join a channel or start a direct message to chat."
                      : "Company-wide announcements"}
                  </p>
                </>
              )}
            </div>
            <div className="flex items-center gap-0.5">
              {/* <Button size="icon" variant="ghost" className="h-8 w-8">
                <Video className="h-4 w-4 text-slate-600" />
              </Button>
              <Button size="icon" variant="ghost" className="h-8 w-8">
                <Phone className="h-4 w-4 text-slate-600" />
              </Button> */}
              <Button
                size="icon"
                variant="ghost"
                className="h-8 w-8"
                onClick={() => {
                  void handleOpenChannelMembersModal();
                }}
                disabled={activeConversation?.type !== "channel"}
                title="Manage channel members"
              >
                <Users className="h-4 w-4 text-slate-600" />
              </Button>
              {/* <Button size="icon" variant="ghost" className="h-8 w-8">
                <Search className="h-4 w-4 text-slate-600" />
              </Button> */}
              <Button size="icon" variant="ghost" className="h-8 w-8">
                <Info className="h-4 w-4 text-slate-600" />
              </Button>
            </div>
          </header>

          {activeConversation?.type === "channel" && (
            <div className="border-b px-5 py-2">
              <div className="flex items-center justify-between gap-2 text-xs">
                <div className="flex items-center gap-2">
                  <span className="text-slate-500">Topics:</span>
                  {topicsForActiveConversation.map(topic => (
                    <button
                      key={topic}
                      type="button"
                      onClick={() => setActiveTopic(topic)}
                      className={`rounded-md border px-2 py-1 text-[11px] font-medium ${
                        activeTopic === topic
                          ? "border-[#0b2f57] bg-[#0b2f57] text-white"
                          : "border-slate-200 bg-white text-slate-600"
                      }`}
                    >
                      # {topic}
                    </button>
                  ))}
                </div>
                <div className="flex items-center gap-1.5">
                  <Input
                    value={newTopicName}
                    onChange={event => setNewTopicName(event.target.value)}
                    placeholder="New topic"
                    className="h-7 w-32 text-[11px]"
                  />
                  <Button
                    type="button"
                    onClick={handleCreateTopic}
                    disabled={creatingTopic || !newTopicName.trim()}
                    className="h-7 px-2 text-[11px]"
                  >
                    {creatingTopic ? "Adding..." : "Add"}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <div
            ref={chatMessagesScrollRef}
            className="scrollbar-thin-grey flex-1 overflow-y-auto px-6 py-4"
          >
            <div className="space-y-6">
              {displayMessages.length === 0 ? (
                <div className="rounded-lg border border-dashed bg-white p-6 text-center text-sm text-slate-500">
                  {activeConversation
                    ? "No messages yet. Start a conversation."
                    : "No chats available. Connect Zulip to load channels and messages."}
                </div>
              ) : (
                messagesWithDateDividers.map(row => {
                  if (row.kind === "divider") {
                    return (
                      <div key={row.id} className="mb-4 flex items-center">
                        <div className="h-px flex-1 bg-slate-200" />
                        <span className="mx-3 rounded-sm border bg-white px-2 py-0.5 text-[11px] text-slate-500">
                          {row.label}
                        </span>
                        <div className="h-px flex-1 bg-slate-200" />
                      </div>
                    );
                  }
                  const message = row.message;
                  const isZulipBackedMessage = message.id.startsWith("zulip-");
                  const isOwnMessage =
                    (zulipUserId !== null &&
                      typeof message.senderZulipUserId === "number" &&
                      message.senderZulipUserId === zulipUserId) ||
                    (!isZulipBackedMessage &&
                      (message.author === "You" ||
                        message.authorCode === "YO"));
                  const isOwnZulipMessage =
                    isZulipBackedMessage &&
                    zulipUserId !== null &&
                    message.senderZulipUserId === zulipUserId;
                  const canEditOwnZulipMessage =
                    isOwnZulipMessage && ptsChatMessageEditEnabled;
                  const canDeleteOwnZulipMessage =
                    isOwnZulipMessage && ptsChatMessageDeleteEnabled;
                  const isEditingThis = editingMessageId === message.id;
                  const isBusyThis = messageMutatingId === message.id;
                  return (
                    <div
                      key={message.id}
                      id={`chat-msg-${message.id}`}
                      className={`flex w-full ${
                        isOwnMessage ? "justify-end" : "items-start gap-3"
                      }`}
                    >
                      {!isOwnMessage && (
                        <Avatar className="h-8 w-8 shrink-0">
                          <AvatarFallback
                            className={`text-[10px] font-semibold text-white ${toneClassMap[message.tone]}`}
                          >
                            {message.authorCode}
                          </AvatarFallback>
                        </Avatar>
                      )}
                      <div
                        className={
                          isOwnMessage
                            ? "w-fit min-w-0 max-w-[min(100%,42rem)] shrink text-left"
                            : "min-w-0 max-w-[min(100%,56rem)] shrink"
                        }
                      >
                        <div
                          className={`mb-0.5 flex flex-wrap items-center gap-2 ${isOwnMessage ? "justify-end" : ""}`}
                        >
                          <span className="text-sm font-semibold text-slate-900">
                            {message.author}
                          </span>
                          <span className="text-[11px] text-slate-500">
                            {message.timeLabel}
                          </span>
                          {message.starredByMe ? (
                            <span
                              className="inline-flex shrink-0 items-center"
                              title="Starred"
                              aria-label="Starred message"
                            >
                              <Star
                                className="h-3.5 w-3.5 fill-amber-400 text-amber-500"
                                aria-hidden
                              />
                            </span>
                          ) : null}
                          {!isEditingThis && activeConversation && (
                            <div
                              className={`relative inline-block text-left ${isOwnMessage ? "" : "ml-auto"}`}
                            >
                              <Button
                                type="button"
                                variant="ghost"
                                size="icon"
                                className="h-7 w-7 text-slate-500 hover:text-slate-800"
                                title="Message actions"
                                disabled={isBusyThis}
                                onClick={() =>
                                  setMessageActionsMenuOpenId(prev =>
                                    prev === message.id ? null : message.id
                                  )
                                }
                              >
                                <MoreVertical className="h-4 w-4" />
                              </Button>
                              {messageActionsMenuOpenId === message.id && (
                                <div
                                  className={`absolute top-full z-40 mt-0.5 min-w-[10.5rem] rounded-md border border-slate-200 bg-white py-1 shadow-lg ${
                                    isOwnMessage ? "right-0" : "left-0"
                                  }`}
                                >
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                                    onClick={() => {
                                      void handleCopyMessageContent(message);
                                    }}
                                  >
                                    <Copy className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                                    Copy content
                                  </button>
                                  <button
                                    type="button"
                                    className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                                    onClick={() => {
                                      void handleReplyToMessage(message);
                                    }}
                                  >
                                    <Reply className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                                    Reply
                                  </button>
                                  {isZulipBackedMessage ? (
                                    <button
                                      type="button"
                                      className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                                      onClick={() => {
                                        void handleToggleZulipMessageStar(
                                          message
                                        );
                                      }}
                                    >
                                      <Star
                                        className={`h-3.5 w-3.5 shrink-0 ${
                                          message.starredByMe
                                            ? "fill-amber-400 text-amber-500"
                                            : "text-slate-500"
                                        }`}
                                      />
                                      {message.starredByMe
                                        ? "Unstar message"
                                        : "Star message"}
                                    </button>
                                  ) : null}
                                  {canEditOwnZulipMessage ||
                                  canDeleteOwnZulipMessage ? (
                                    <>
                                      <div className="my-1 border-t border-slate-100" />
                                      {canEditOwnZulipMessage ? (
                                        <button
                                          type="button"
                                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-slate-700 hover:bg-slate-50"
                                          onClick={() => {
                                            setMessageActionsMenuOpenId(null);
                                            handleStartEditMessage(message);
                                          }}
                                        >
                                          <Pencil className="h-3.5 w-3.5 shrink-0 text-slate-500" />
                                          Edit
                                        </button>
                                      ) : null}
                                      {canDeleteOwnZulipMessage ? (
                                        <button
                                          type="button"
                                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-xs text-red-600 hover:bg-red-50"
                                          onClick={() => {
                                            setMessageActionsMenuOpenId(null);
                                            if (
                                              message.senderZulipUserId !==
                                              zulipUserId
                                            ) {
                                              return;
                                            }
                                            setDeleteConfirmMessageId(
                                              message.id
                                            );
                                            setDeleteConfirmSenderZulipId(
                                              message.senderZulipUserId
                                            );
                                          }}
                                        >
                                          <Trash2 className="h-3.5 w-3.5 shrink-0" />
                                          Delete
                                        </button>
                                      ) : null}
                                    </>
                                  ) : null}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        {isEditingThis ? (
                          <div className="mt-1.5 w-full max-w-md text-left">
                            <div className="rounded-md border border-slate-200 bg-white p-2 shadow-sm ring-1 ring-slate-900/[0.04]">
                              <p className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
                                Edit message
                              </p>
                              {messageEditError && (
                                <div
                                  role="alert"
                                  className="mb-1.5 rounded border border-red-200 bg-red-50 px-2 py-1.5 text-[11px] leading-snug text-red-800"
                                >
                                  {messageEditError}
                                </div>
                              )}
                              <Textarea
                                ref={messageEditTextareaRef}
                                value={editingMessageDraft}
                                onChange={event => {
                                  setMessageEditError(null);
                                  setEditingMessageDraft(event.target.value);
                                }}
                                rows={2}
                                placeholder="Message text"
                                className="max-h-36 min-h-[44px] resize-y border-slate-200 py-2 text-sm leading-snug focus-visible:ring-[#0b2f57]"
                                disabled={isBusyThis}
                                aria-label="Edit message text"
                                onKeyDown={event => {
                                  if (event.key === "Escape") {
                                    event.preventDefault();
                                    handleCancelEditMessage();
                                    return;
                                  }
                                  if (
                                    event.key === "Enter" &&
                                    (event.ctrlKey || event.metaKey)
                                  ) {
                                    event.preventDefault();
                                    if (
                                      !isBusyThis &&
                                      editingMessageDraft.trim()
                                    ) {
                                      void handleSaveEditMessage();
                                    }
                                  }
                                }}
                              />
                              <div className="mt-2 flex flex-col gap-2 border-t border-slate-100 pt-2 sm:flex-row sm:items-center sm:justify-between">
                                <p className="order-2 text-[10px] leading-tight text-slate-400 sm:order-1">
                                  <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-px font-mono text-[9px] text-slate-600">
                                    Esc
                                  </kbd>{" "}
                                  cancel ·{" "}
                                  <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-px font-mono text-[9px] text-slate-600">
                                    ⌘
                                  </kbd>
                                  /
                                  <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-px font-mono text-[9px] text-slate-600">
                                    Ctrl
                                  </kbd>
                                  +
                                  <kbd className="rounded border border-slate-200 bg-slate-50 px-1 py-px font-mono text-[9px] text-slate-600">
                                    Enter
                                  </kbd>{" "}
                                  save
                                </p>
                                <div className="order-1 flex w-full justify-end gap-1.5 sm:order-2 sm:w-auto">
                                  <Button
                                    type="button"
                                    variant="outline"
                                    size="sm"
                                    className="h-7 min-w-[3.75rem] px-2.5 text-[11px]"
                                    disabled={isBusyThis}
                                    onClick={handleCancelEditMessage}
                                  >
                                    Cancel
                                  </Button>
                                  <Button
                                    type="button"
                                    size="sm"
                                    className="h-7 min-w-[3.75rem] bg-[#0b2f57] px-2.5 text-[11px] hover:bg-[#0a2747]"
                                    disabled={
                                      isBusyThis || !editingMessageDraft.trim()
                                    }
                                    onClick={() => {
                                      void handleSaveEditMessage();
                                    }}
                                  >
                                    {isBusyThis ? "Saving…" : "Save"}
                                  </Button>
                                </div>
                              </div>
                            </div>
                          </div>
                        ) : message.htmlContent ? (
                          (() => {
                            const parsed = parseZulipQuoteReplyFromText(
                              message.text ?? ""
                            );
                            if (parsed) {
                              return (
                                <ZulipQuoteReplyBubble
                                  message={message}
                                  parsed={parsed}
                                  authHeader={zulipBasicAuthHeader}
                                  onImageClick={handleZulipMessageImageClick}
                                />
                              );
                            }
                            const useReplyFallback = /<blockquote/i.test(
                              message.htmlContent
                            );
                            return (
                              <div
                                className={`zulip-message-content max-w-full text-left text-sm leading-relaxed text-slate-700 [overflow-wrap:anywhere] ${
                                  useReplyFallback
                                    ? "pts-zulip-reply-fallback"
                                    : ""
                                } ${
                                  (isOwnMessage && !isEditingThis) ||
                                  (useReplyFallback && !isOwnMessage)
                                    ? "rounded-2xl border border-slate-200/70 bg-slate-50/90 px-3 py-2"
                                    : ""
                                }`}
                                role="presentation"
                                data-preview-sender={message.author}
                                data-preview-time={message.timeLabel}
                                data-preview-message-id={message.id}
                                data-preview-sort-key={String(
                                  message.sortKey ?? Date.now()
                                )}
                                onClick={handleZulipMessageImageClick}
                                dangerouslySetInnerHTML={{
                                  __html: message.htmlContent,
                                }}
                              />
                            );
                          })()
                        ) : (
                          <p
                            className={`max-w-full text-left text-sm leading-relaxed text-slate-700 [overflow-wrap:anywhere] ${isOwnMessage && !isEditingThis ? "rounded-2xl border border-slate-200/70 bg-slate-50/90 px-3 py-2" : ""}`}
                          >
                            <ZulipPlainMessageText text={message.text} />
                          </p>
                        )}
                        {!isEditingThis && (
                          <div
                            className={`relative mt-2 flex flex-wrap items-center gap-1.5 ${isOwnMessage ? "justify-end" : ""}`}
                          >
                            {(message.reactions ?? []).length > 0 &&
                              (activeConversation?.type === "dm"
                                ? (message.reactions ?? []).slice(0, 1)
                                : (message.reactions ?? [])
                              ).map(reaction => (
                                <button
                                  key={`${message.id}-${reaction.emoji}`}
                                  type="button"
                                  onClick={() => {
                                    void handleReactionClick(
                                      message.id,
                                      reaction.emoji,
                                      EMOJI_NAME_BY_SYMBOL[reaction.emoji] ??
                                        "smile"
                                    );
                                  }}
                                  className="rounded-md border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-700"
                                >
                                  {activeConversation?.type === "dm"
                                    ? reaction.emoji
                                    : `${reaction.emoji} ${reaction.count}`}
                                </button>
                              ))}
                            <button
                              type="button"
                              onClick={() =>
                                setReactionPickerForMessage(prev => {
                                  const next =
                                    prev === message.id ? null : message.id;
                                  return next;
                                })
                              }
                              className="rounded-full border border-slate-200 bg-white px-2 py-0.5 text-xs text-slate-600 hover:bg-slate-50"
                            >
                              <SmilePlus size={15} />
                            </button>
                            {reactionPickerForMessage === message.id && (
                              <div className="absolute top-8 z-20 w-[300px] rounded-2xl border border-slate-200 bg-white p-3 shadow-xl">
                                <div className="scrollbar-thin-grey max-h-72 space-y-3 overflow-y-auto pr-1">
                                  {REACTION_PICKER_SECTIONS.map(section => (
                                    <div key={`${message.id}-${section.title}`}>
                                      <p className="mb-1 text-[11px] font-extrabold tracking-wide text-slate-500">
                                        {section.title}
                                      </p>
                                      <div className="flex flex-wrap gap-1.5">
                                        {section.emojis.map(emoji => {
                                          const emojiName =
                                            EMOJI_NAME_BY_SYMBOL[emoji] ??
                                            "smile";
                                          return (
                                            <button
                                              key={`${message.id}-picker-${section.title}-${emoji}`}
                                              type="button"
                                              onClick={() => {
                                                void handleSelectMessageReaction(
                                                  message.id,
                                                  emoji,
                                                  emojiName
                                                );
                                              }}
                                              className="rounded-md px-1.5 py-1 text-xl leading-none transition-colors hover:bg-slate-100"
                                              title={emojiName}
                                            >
                                              {emoji}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>

          <footer className="border-t bg-white px-5 pb-4 pt-2">
            {activeConversation?.type === "channel" && (
              <div className="mb-2 flex items-center gap-2 text-xs text-slate-500">
                <Hash className="h-3.5 w-3.5" />
                <span>
                  Topic (defaults to{" "}
                  <span className="font-semibold text-slate-700">{`'${activeTopic}'`}</span>
                  )
                </span>
              </div>
            )}
            <div className="overflow-hidden rounded-md border border-slate-200 bg-white shadow-sm">
              {composerReplyStrip && (
                <div className="flex items-center justify-between gap-2 border-b border-emerald-200/80 bg-emerald-50/90 px-3 py-2">
                  <div className="flex min-w-0 flex-1 items-center gap-2 text-xs text-emerald-950">
                    <Reply className="h-3.5 w-3.5 shrink-0 text-emerald-700" />
                    <span className="min-w-0 leading-snug">
                      <span className="font-semibold">Replying to </span>
                      <span className="font-medium">
                        {composerReplyStrip.replyToLabel}
                      </span>
                      <span className="text-emerald-800/90">
                        {" "}
                        · Enter sends · Shift+Enter new line
                      </span>
                    </span>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="h-7 shrink-0 gap-1 px-2 text-xs text-emerald-900 hover:bg-emerald-100/80"
                    onClick={handleCancelComposerReply}
                  >
                    <X className="h-3.5 w-3.5" />
                    Cancel
                  </Button>
                </div>
              )}
              {showComposerFormattingToolbar && (
                <div className="flex flex-wrap items-center gap-0.5 border-b border-slate-200 bg-slate-50 px-1.5 py-1">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Bold (Markdown)"
                    onClick={() => applyComposerMarkdownWrap("**", "**")}
                  >
                    <Bold className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Italic"
                    onClick={() => applyComposerMarkdownWrap("*", "*")}
                  >
                    <Italic className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Underline (not supported in Zulip)"
                    disabled
                  >
                    <Baseline className="h-4 w-4 opacity-40" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Strikethrough"
                    onClick={() => applyComposerMarkdownWrap("~~", "~~")}
                  >
                    <Strikethrough className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Bulleted list"
                    onClick={() =>
                      applyComposerLineMap(line =>
                        /^\s*-\s/.test(line) ? line : `- ${line}`
                      )
                    }
                  >
                    <List className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Numbered list (first line)"
                    onClick={() =>
                      applyComposerLineMap((line, idx) =>
                        idx === 0
                          ? /^\s*\d+\.\s/.test(line)
                            ? line
                            : `1. ${line.replace(/^\s*\d+\.\s*/, "")}`
                          : line
                      )
                    }
                  >
                    <ListOrdered className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Decrease indent"
                    onClick={() =>
                      applyComposerLineMap(line =>
                        line.replace(/^ {1,4}/, "").replace(/^\t/, "")
                      )
                    }
                  >
                    <IndentDecrease className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Increase indent"
                    onClick={() =>
                      applyComposerLineMap(line =>
                        line.length === 0 ? line : `    ${line}`
                      )
                    }
                  >
                    <IndentIncrease className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Insert link"
                    onClick={() => insertComposerMarkdownLink()}
                  >
                    <Link2 className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Quote"
                    onClick={() =>
                      applyComposerLineMap(line =>
                        /^\s*>/.test(line) ? line : `> ${line}`
                      )
                    }
                  >
                    <Quote className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Inline code"
                    onClick={() => applyComposerMarkdownWrap("`", "`")}
                  >
                    <Code className="h-4 w-4" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className={composerToolbarButtonClass}
                    title="Code block"
                    onClick={() => insertComposerFencedCode()}
                  >
                    <Braces className="h-4 w-4" />
                  </Button>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className={composerToolbarButtonClass}
                        title="More formatting"
                      >
                        <MoreHorizontal className="h-4 w-4" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      className="min-w-[10rem]"
                    >
                      <DropdownMenuItem
                        onSelect={() => insertComposerHeading()}
                      >
                        Heading (##)
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        onSelect={() => insertComposerHorizontalRule()}
                      >
                        Horizontal line (---)
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <div className="flex flex-1 justify-end">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className={`${composerToolbarButtonClass} text-slate-500 hover:bg-slate-200 hover:text-slate-900`}
                      title="Close formatting toolbar"
                      onClick={() => setShowComposerFormattingToolbar(false)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
              <div className="border-b border-slate-100 bg-slate-50/70 px-2 py-2">
                <Textarea
                  ref={messageInputRef}
                  value={messageInput}
                  onChange={event =>
                    handleMessageInputChange(
                      event.target.value,
                      event.target.selectionStart ?? event.target.value.length
                    )
                  }
                  onKeyDown={event => {
                    if (
                      (event.ctrlKey || event.metaKey) &&
                      event.key.toLowerCase() === "b"
                    ) {
                      event.preventDefault();
                      applyComposerMarkdownWrap("**", "**");
                      return;
                    }
                    if (
                      (event.ctrlKey || event.metaKey) &&
                      event.key.toLowerCase() === "i"
                    ) {
                      event.preventDefault();
                      applyComposerMarkdownWrap("*", "*");
                      return;
                    }
                    if (showMentionSuggestions) {
                      if (event.key === "ArrowDown") {
                        event.preventDefault();
                        setActiveMentionIndex(prev =>
                          Math.min(prev + 1, filteredMentionOptions.length - 1)
                        );
                        return;
                      }
                      if (event.key === "ArrowUp") {
                        event.preventDefault();
                        setActiveMentionIndex(prev => Math.max(prev - 1, 0));
                        return;
                      }
                      if (event.key === "Enter" || event.key === "Tab") {
                        event.preventDefault();
                        const selected =
                          filteredMentionOptions[activeMentionIndex] ??
                          filteredMentionOptions[0];
                        if (selected) {
                          insertMention(selected);
                        }
                        return;
                      }
                      if (event.key === "Escape") {
                        event.preventDefault();
                        closeMentionSuggestions();
                        return;
                      }
                    }
                    if (event.key === "Enter" && !event.shiftKey) {
                      event.preventDefault();
                      handleSendToActiveConversation();
                    }
                  }}
                  placeholder={
                    composerReplyStrip
                      ? "Write your reply below the quote…"
                      : "Type a message... (Shift+Enter for new line)"
                  }
                  disabled={!activeConversation}
                  className="min-h-[60px] resize-none border-0 bg-transparent px-1 py-1 text-sm shadow-none focus-visible:ring-0"
                />
              </div>
              {showMentionSuggestions && (
                <div className="scrollbar-thin-grey mx-2 mb-2 max-h-44 overflow-y-auto rounded-md border border-slate-200 bg-white shadow-sm">
                  {filteredMentionOptions.slice(0, 8).map((option, index) => (
                    <button
                      key={option.id}
                      type="button"
                      onClick={() => insertMention(option)}
                      className={`flex w-full items-center justify-between px-2 py-1.5 text-left text-sm ${
                        index === activeMentionIndex
                          ? "bg-slate-100 text-slate-900"
                          : "text-slate-700 hover:bg-slate-50"
                      }`}
                    >
                      <span>{option.label}</span>
                      <span className="text-xs text-slate-400">@ mention</span>
                    </button>
                  ))}
                </div>
              )}
              <div className="relative flex flex-wrap items-center justify-between gap-x-2 gap-y-1.5 px-2 pb-2 pt-2">
                {showEmojiPicker && (
                  <div className="absolute bottom-full left-0 z-20 mb-1 flex gap-1 rounded-md border border-slate-200 bg-white p-1 shadow-md">
                    {QUICK_EMOJIS.map(emoji => (
                      <button
                        key={emoji}
                        type="button"
                        onClick={() => handleEmojiSelect(emoji)}
                        className="rounded px-1.5 py-1 text-base hover:bg-slate-100"
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                )}
                <div className="flex flex-wrap items-center gap-1 text-slate-500">
                  <input
                    ref={attachmentInputRef}
                    type="file"
                    className="hidden"
                    onChange={event => {
                      void handleAttachmentUpload(event);
                    }}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className={`h-7 w-7 ${showComposerFormattingToolbar ? "bg-slate-200 text-slate-900" : ""}`}
                    title={
                      showComposerFormattingToolbar
                        ? "Hide formatting toolbar"
                        : "Show formatting toolbar"
                    }
                    onClick={() =>
                      setShowComposerFormattingToolbar(prev => !prev)
                    }
                  >
                    <ALargeSmall className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => attachmentInputRef.current?.click()}
                    disabled={uploadingAttachment || !activeConversation}
                    title={uploadingAttachment ? "Uploading..." : "Attach file"}
                  >
                    <Paperclip className="h-4 w-4" />
                  </Button>
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => setShowEmojiPicker(prev => !prev)}
                  >
                    <Smile className="h-4 w-4" />
                  </Button>
                  {/* <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    disabled
                    title="GIF picker is not available"
                  >
                    <span className="text-[9px] font-bold leading-none tracking-tight">
                      GIF
                    </span>
                  </Button> */}
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7"
                    onClick={() => {
                      appendToMessage("@");
                      requestAnimationFrame(() => {
                        const textarea = messageInputRef.current;
                        if (!textarea) {
                          return;
                        }
                        textarea.focus();
                        const caret =
                          textarea.selectionStart ?? textarea.value.length;
                        syncMentionQueryFromInput(textarea.value, caret);
                      });
                    }}
                  >
                    <AtSign className="h-4 w-4" />
                  </Button>
                </div>
                <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:flex-initial sm:gap-3">
                  {/* <p className="hidden min-w-0 flex-1 truncate text-right text-[10px] leading-snug text-slate-400 sm:block">
                    Formatting inserts Zulip markdown.
                  </p> */}
                  <div
                    className="hidden h-5 w-px shrink-0 bg-slate-200 sm:block"
                    aria-hidden
                  />
                  <Button
                    onClick={handleSendToActiveConversation}
                    disabled={
                      !activeConversation ||
                      sendingDmMessage ||
                      uploadingAttachment
                    }
                    className="h-8 shrink-0 bg-[#0b2f57] px-3 text-xs hover:bg-[#0a2747]"
                  >
                    <Send className="mr-1.5 h-3.5 w-3.5" />
                    {uploadingAttachment
                      ? "Uploading..."
                      : sendingDmMessage
                        ? "Sending..."
                        : "Send"}
                  </Button>
                </div>
              </div>
            </div>
          </footer>
          {chatImagePreview && (
            <div
              className="absolute inset-0 z-50 flex items-center justify-center bg-black/30 p-3 backdrop-blur-sm"
              role="presentation"
              onClick={closeChatImagePreview}
            >
              <div
                className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="chat-image-preview-title"
                onClick={event => event.stopPropagation()}
              >
                <header className="flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-3 sm:gap-3 sm:px-4">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 shrink-0 text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                    aria-label="Back"
                    onClick={closeChatImagePreview}
                  >
                    <ArrowLeft className="h-5 w-5" />
                  </Button>
                  <div className="min-w-0 flex-1">
                    <h2
                      id="chat-image-preview-title"
                      className="truncate text-sm font-semibold text-slate-900"
                    >
                      {chatImagePreview.senderName}
                    </h2>
                    <p className="mt-0.5 truncate text-[11px] leading-snug text-slate-500">
                      image | {formatRelativeDayLabel(chatImagePreview.sortKey)}{" "}
                      {chatImagePreview.timeLabel.toLowerCase()}
                      {" · "}
                      <button
                        type="button"
                        className="text-primary font-medium hover:text-[#0a2747] hover:underline"
                        onClick={showMessageFromImagePreview}
                      >
                        Show message
                      </button>
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-0.5 sm:gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
                      aria-label="Zoom out"
                      disabled={chatImagePreviewZoom <= 0.5}
                      onClick={() =>
                        setChatImagePreviewZoom(z =>
                          Math.max(0.5, Math.round((z - 0.25) * 100) / 100)
                        )
                      }
                    >
                      <ZoomOut className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-40"
                      aria-label="Zoom in"
                      disabled={chatImagePreviewZoom >= 3}
                      onClick={() =>
                        setChatImagePreviewZoom(z =>
                          Math.min(3, Math.round((z + 0.25) * 100) / 100)
                        )
                      }
                    >
                      <ZoomIn className="h-4 w-4" />
                    </Button>
                    <div
                      className="mx-1 hidden h-6 w-px shrink-0 bg-slate-200 sm:block"
                      aria-hidden
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-9 w-9 text-slate-600 hover:bg-slate-100 hover:text-slate-900 disabled:opacity-50"
                      aria-label="Download image"
                      disabled={chatImageDownloadBusy}
                      onClick={() => {
                        void downloadChatPreviewImage();
                      }}
                    >
                      <Download className="h-4 w-4" />
                    </Button>
                  </div>
                </header>
                <div className="scrollbar-thin-grey flex min-h-0 flex-1 items-center justify-center overflow-auto border-t border-slate-100 bg-slate-50 p-3 sm:p-5">
                  <div
                    className="flex max-h-full max-w-full items-center justify-center transition-transform duration-150 ease-out"
                    style={{
                      transform: `scale(${chatImagePreviewZoom})`,
                      transformOrigin: "center center",
                    }}
                  >
                    <img
                      src={chatImagePreview.src}
                      alt={chatImagePreview.alt}
                      className="max-h-[min(78vh,100%)] max-w-full rounded-md border border-slate-200 bg-white object-contain shadow-sm"
                      draggable={false}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
          {deleteConfirmMessageId && ptsChatMessageDeleteEnabled && (
            <div
              className="absolute inset-0 z-40 flex items-center justify-center bg-black/30 p-3 backdrop-blur-sm"
              role="presentation"
              onClick={() => {
                setDeleteConfirmMessageId(null);
                setDeleteConfirmSenderZulipId(null);
              }}
            >
              <div
                className="w-full max-w-sm rounded-lg bg-white p-4 shadow-xl"
                role="dialog"
                aria-modal="true"
                aria-labelledby="delete-message-title"
                onClick={event => event.stopPropagation()}
              >
                <h3
                  id="delete-message-title"
                  className="text-sm font-semibold text-slate-900"
                >
                  Delete message?
                </h3>
                <p className="mt-2 text-xs leading-relaxed text-slate-600">
                  Are you sure you want to delete this message?
                </p>
                <div className="mt-4 flex justify-end gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-8 px-3 text-xs"
                    disabled={messageMutatingId === deleteConfirmMessageId}
                    onClick={() => {
                      setDeleteConfirmMessageId(null);
                      setDeleteConfirmSenderZulipId(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 bg-red-600 px-3 text-xs hover:bg-red-700"
                    disabled={messageMutatingId === deleteConfirmMessageId}
                    onClick={() => {
                      void performMessageDeletion(deleteConfirmMessageId);
                    }}
                  >
                    {messageMutatingId === deleteConfirmMessageId
                      ? "Deleting..."
                      : "Delete"}
                  </Button>
                </div>
              </div>
            </div>
          )}
          {showChannelMembersModal && (
            <div className="absolute inset-0 z-30 flex items-center justify-center bg-black/30 p-3 backdrop-blur-sm">
              <div className="w-full max-w-xl rounded-lg bg-white p-4 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                  <div>
                    <h3 className="text-sm font-semibold text-slate-900">
                      Manage Channel Members
                    </h3>
                    <p className="text-xs text-slate-500">
                      {activeConversation?.name ?? "Channel"}
                    </p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 px-2 text-xs"
                    onClick={() => setShowChannelMembersModal(false)}
                  >
                    Close
                  </Button>
                </div>

                <Input
                  value={channelMemberSearchText}
                  onChange={event =>
                    setChannelMemberSearchText(event.target.value)
                  }
                  placeholder="Search members..."
                  className="mb-3 h-8"
                />

                <div className="mb-2 flex items-center justify-between rounded-md border border-slate-200 bg-slate-50 px-2 py-1.5 text-xs">
                  <span className="text-slate-600">
                    Total members in channel
                  </span>
                  <span className="rounded bg-white px-2 py-0.5 font-semibold text-slate-800">
                    {activeChannelMemberIds.size}
                  </span>
                </div>

                <div className="scrollbar-thin-grey max-h-[22rem] space-y-3 overflow-y-auto rounded-md border p-2">
                  {loadingChannelMembers ? (
                    <p className="py-6 text-center text-xs text-slate-500">
                      Loading members...
                    </p>
                  ) : channelMemberCandidates.length === 0 ? (
                    <p className="py-6 text-center text-xs text-slate-500">
                      No members found.
                    </p>
                  ) : (
                    <>
                      <div>
                        <div className="mb-1 flex items-center justify-between px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          <span>In Channel</span>
                          <span>{usersInActiveChannel.length}</span>
                        </div>
                        <div className="space-y-1">
                          {usersInActiveChannel.length === 0 ? (
                            <p className="rounded-md border border-dashed px-2 py-2 text-xs text-slate-500">
                              No members in this section.
                            </p>
                          ) : (
                            usersInActiveChannel.map(user => (
                              <div
                                key={`channel-member-in-${user.id}`}
                                className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50"
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <div className="relative h-7 w-7 shrink-0">
                                    <Avatar className="h-7 w-7">
                                      <AvatarFallback className="bg-violet-100 text-[10px] font-semibold text-violet-700">
                                        {user.name
                                          .split(" ")
                                          .map(part => part[0] ?? "")
                                          .join("")
                                          .slice(0, 2)
                                          .toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span
                                      className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                                        getPresenceStatus(user.zulipUserId) ===
                                        "active"
                                          ? "bg-emerald-500"
                                          : getPresenceStatus(
                                                user.zulipUserId
                                              ) === "idle"
                                            ? "bg-amber-400"
                                            : "bg-slate-300"
                                      }`}
                                      title={`Status: ${getPresenceStatus(user.zulipUserId)}`}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <span className="block truncate text-sm text-slate-700">
                                      {user.name}
                                    </span>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  variant="outline"
                                  className="h-7 px-2 text-xs"
                                  disabled={
                                    !user.zulipUserId ||
                                    updatingChannelMemberUserId ===
                                      user.zulipUserId
                                  }
                                  onClick={() => {
                                    void handleChannelMemberUpdate(
                                      "remove",
                                      user
                                    );
                                  }}
                                >
                                  {updatingChannelMemberUserId ===
                                  user.zulipUserId
                                    ? "Updating..."
                                    : "Remove"}
                                </Button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>

                      <div>
                        <div className="mb-1 flex items-center justify-between px-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                          <span>Available Members</span>
                          <span>{usersOutsideActiveChannel.length}</span>
                        </div>
                        <div className="space-y-1">
                          {usersOutsideActiveChannel.length === 0 ? (
                            <p className="rounded-md border border-dashed px-2 py-2 text-xs text-slate-500">
                              No members in this section.
                            </p>
                          ) : (
                            usersOutsideActiveChannel.map(user => (
                              <div
                                key={`channel-member-out-${user.id}`}
                                className="flex items-center justify-between rounded-md px-2 py-1.5 hover:bg-slate-50"
                              >
                                <div className="flex min-w-0 items-center gap-2">
                                  <div className="relative h-7 w-7 shrink-0">
                                    <Avatar className="h-7 w-7">
                                      <AvatarFallback className="bg-violet-100 text-[10px] font-semibold text-violet-700">
                                        {user.name
                                          .split(" ")
                                          .map(part => part[0] ?? "")
                                          .join("")
                                          .slice(0, 2)
                                          .toUpperCase()}
                                      </AvatarFallback>
                                    </Avatar>
                                    <span
                                      className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-white shadow-sm ${
                                        getPresenceStatus(user.zulipUserId) ===
                                        "active"
                                          ? "bg-emerald-500"
                                          : getPresenceStatus(
                                                user.zulipUserId
                                              ) === "idle"
                                            ? "bg-amber-400"
                                            : "bg-slate-300"
                                      }`}
                                      title={`Status: ${getPresenceStatus(user.zulipUserId)}`}
                                    />
                                  </div>
                                  <div className="min-w-0">
                                    <span className="block truncate text-sm text-slate-700">
                                      {user.name}
                                    </span>
                                  </div>
                                </div>
                                <Button
                                  type="button"
                                  className="h-7 px-2 text-xs"
                                  disabled={
                                    !user.zulipUserId ||
                                    updatingChannelMemberUserId ===
                                      user.zulipUserId
                                  }
                                  onClick={() => {
                                    void handleChannelMemberUpdate("add", user);
                                  }}
                                >
                                  {updatingChannelMemberUserId ===
                                  user.zulipUserId
                                    ? "Updating..."
                                    : "Add"}
                                </Button>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          )}
        </section>
      </div>
    </div>
  );
};

export default ChatPage;
